import { campaignLightForClock } from '../shared/campaign-light';
import { calendarInventoryDue, calendarRoundDue, resolveCalendarCatchup, type CalendarFacts } from '../janus-engine/turns/calendar-catchup.js';
import { ecId, ecLean, itemField } from '../janus-engine/ability/context.js';
import { encounterLightTypesPresent } from '../encounter-map/ft3d/light-presence.js';
import { entityCompileReady } from '../shared/entity-compile-ready';
import { withLogParticipants } from '../shared/log-participant-snapshot';
import { statusScalar } from '../janus-engine/actions/activity.js';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { buildAppliedFrame } from '../janus-engine/transport/applied-frame';
import { withAsyncDeadline } from '../shared/async-deadline';
import { refreshTimeConflict } from './time-conflict-refresh';
// R5a: every time-push chunk is decided in the browser and posted through the clock truth save.
// Unsupported effects become GM-only skip rows while the clock advances. Missing save prerequisites
// and rejected saves return null; ambiguous saves return 'unresolved' so the caller stops later chunks.
// A network retry keeps the same operation id. No outcome posts to a legacy time rule route.
//
// Clock updates patch the held campaign through janus_time_truth. Saved effect frames follow janus_time_truth and are also
// applied from the sender's successful answer, once per operation id.
import { resolveTimePush, resolveTimePushAsync, chargeTicksFor, AIRBORNE_SPEED_IDS, HUNGER_APPLY_IDS, HUNGER_IDS, TIME_PUSH_SCHEMA, TIME_PUSH_RULES_CALENDAR, TIME_PUSH_MAX_ROUNDS, type TimePushCommand, type TimePushRowFrame } from '../janus-engine/turns/time-push.js';
import { resolveOocRoundPassAsync, emptyTurnTick, oocRoundDue, pendingOocRounds, yieldTimeTask } from '../janus-engine/turns/ooc-round-pass.js';
import { setRowAbilityCharges } from '../janus-engine/ability/charges/charge-entry.js';
import { applyStatusFrame } from '../janus-engine/turns/status-frame.js';
import { applyStatusRootFields } from '../janus-engine/compile/status-root-residue.js';
import { removeStatusFromRow, statusPaused, statusRows, statusEndSeconds, statusItemId, STATUS_IDS } from '../janus-engine/statuses/expiry.js';
import type { EndTurnReaders } from '../janus-engine/turns/end-turn.js';
import { ftClockCalendarFromArr, ftClockCampaignField, ftClockColsFromArr, ftClockBaseFromArr, ftClockParseUtc, ftDeriveCampaignClock, ftCampaignTimeAddSeconds, ftCampaignTimeSubtractSeconds, type CampaignClockCols } from '../shared/campaign-clock.js';

import { markStall } from '../shared/stall-mark';
const fallbackClockAnchors = new WeakMap<object, { stamp: number; receivedAt: number }>();
function fallbackClockAnchorOf(campaign: Row, reset = false): { stamp: number; receivedAt: number } | null {
  const stamp = ftClockParseUtc(asRow(asRow(campaign.computed)?.campaign)?.server_utc_now);
  if (stamp === null) return null;
  let anchor = reset ? undefined : fallbackClockAnchors.get(campaign);
  if (!anchor || anchor.stamp !== stamp) {
    anchor = { stamp, receivedAt: performance.now() };
    fallbackClockAnchors.set(campaign, anchor);
  }
  return anchor;
}
/** The widget clock, else the server-stamped blob plus monotonic elapsed time. */
function derivedClockOf(campaign: Row): CampaignClockCols {
  const derived = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
  if (derived && Number.isFinite(Number(derived.year))) return { year: Number(derived.year), month: Number(derived.month), day: Number(derived.day), hour: Number(derived.hour), minute: Number(derived.minute), second: Number(derived.second) };
  const base = ftClockBaseFromArr(campaign);
  const anchor = fallbackClockAnchorOf(campaign);
  return anchor ? ftDeriveCampaignClock(base, anchor.stamp + Math.max(0, performance.now() - anchor.receivedAt)).cols : base.cols;
}
const hungerEnabledOf = (campaign: Row): boolean => Number(scalar(ftClockCampaignField(campaign, 'hunger_thirst_enabled')) ?? 0) !== 0;
import { encounterField, hasPlayerPermission, layerListEntryUuid, type CasterArr } from '../shared/array-types.js';
import { handleCasterUpdate } from './caster-update.js';
import { fetchAndCacheObject } from './object-cache.js';
import { turnReaders, prefetchTickDefinitions, retireTickCreatures } from './turn-truth.js';
import { applyTurnTick, prepareTurnTick } from '../janus-engine/turns/tick-frame.js';
import type { TurnTickResult } from '../janus-engine/turns/ticks.js';
import { HandBack } from '../janus-engine/ability/context.js';
import { createTriggerConsent, planWithConsent, offerFortunate } from './trigger-consent.js';
import { _ftCamField } from '../shared/campaign-field.js';
import { skipUnported, unportedSkipRow, withQuietUnported } from '../janus-engine/unported.js';
import { reportTimePushRefusal } from '../janus-engine/telemetry/time-push-refusal.js';
import { participantCompileRefusal } from '../janus-engine/ability/participant-compile-refusal.js';
import { commitDueZoneExpiries } from './gm-boundary-truth.js';
import { prepareDeathResources } from '../shared/spawn-browser.js';
import { holdsTaggedSummon } from '../janus-engine/death/summons.js';
import { hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { MEMBER_TIME_KIND, memberTimeScene, memberTimePeers, memberTimeStatusFingerprint, memberTimeRefusal, type MemberTimeInterval } from '../janus-engine/turns/member-time-debt.js';
import { pendingTimeAcknowledgment, planPendingTimeSpan } from '../janus-engine/turns/pending-time-span.js';

const ENDPOINT = '/options/game/actions/time_truth_commit_inc.php';
type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);
async function settleCrafts(input: { encounterId: string; clock: CampaignClockCols }): Promise<void> {
  const bridge = (window as unknown as { ftSettleObservedCrafts?: (input: { encounterId: string; clock: CampaignClockCols }) => Promise<'handled' | 'unresolved'> }).ftSettleObservedCrafts;
  // Craft receipts belong to the activity observer. A missing observer has no work;
  // a failed craft retries on the next cadence without parking the saved clock.
  try { await bridge?.(input); } catch { /* the next cadence retries */ }
}
async function prepareRoundDeaths(encounter: Row, campaign: Row) {
  const read = turnReaders(encounter);
  const roster = rosterOf(encounter);
  if (!roster.some(row => oocRoundDue(row, read) || statusRows(row, 'target').some(s => [STATUS_IDS.drowning, STATUS_IDS.suffocating].includes(statusItemId(s) as never)))
    && !(read.zoneStatusRows?.() ?? []).some(status => !statusPaused(status) && Number(status.status_aoe_source ?? 0) !== 0)
    && !holdsTaggedSummon(roster)) return undefined;
  return await prepareDeathResources(roster, encounter, {
    value: (row, key) => row ? read.value(row, key) : null,
    entries: (row, key) => row ? read.entries(row, key) : [], definition: read.definition,
  }, campaign);
}

function campaignFor(id: string): Row | null { return asRow(window.FateTide?.campaignCache?.get?.(id) ?? window.FateTide?.campaignCache?.get?.(upper(id))); }
function encounterFor(id: string): Row | null { return asRow(window.FateTide?.encounterCache?.get?.(id) ?? window.FateTide?.encounterCache?.get?.(upper(id))); }
function inCombatOf(encounter: Row | null): boolean | null {
  if (!encounter) return null;
  const bucket = asRow(encounter.encounters);
  const raw = scalar(bucket && 'in_combat' in bucket ? bucket.in_combat : encounter.in_combat);
  if (raw === undefined || raw === null) return bucket ? false : null; // the lean builder elides in_combat at 0
  return Number(raw) === 1;
}
function runningSinceOf(campaign: Row): string | null {
  const raw = ftClockCampaignField(campaign, 'campaign_clock_running_since');
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
}
function rosterOf(encounter: Row | null): Row[] { return Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as Row[] : []; }
function rowFor(encounter: Row | null, id: string): Row | null { return rosterOf(encounter).find(r => upper(asRow(r.encounter_creatures)?.encounter_creature_id) === upper(id)) ?? null; }
/** Recovery can replace the roster. Read its member ids only after the encounter
 * is adopted; tick/calendar routines below hydrate the inputs they actually use. */
type TimeRowsRefusal = { reason: string; detail: Row };
async function awaitTimeActionRows(encounterId: string): Promise<TimeRowsRefusal | null> {
  const unavailable = (reason = 'entity-base-unavailable'): TimeRowsRefusal => {
    const encounter = encounterFor(encounterId);
    for (const member of rosterOf(encounter)) {
      const id = ecId(member);
      const { memberName, rowRefusal } = participantCompileRefusal(encounter!, member, id);
      if (rowRefusal && !/^unported:[a-z]+-base-pending$/.test(rowRefusal)) return {
        reason: 'entity-compile-refused:' + rowRefusal,
        detail: { actorId: id, carrier: id, memberName, compileReason: rowRefusal },
      };
    }
    const cause = encounter?._entity_refusal;
    return typeof cause === 'string' && !/^unported:[a-z]+-base-pending$/.test(cause)
      ? { reason: 'entity-compile-refused:' + cause, detail: {
        memberName: String(encounterField(encounter, 'encounter_name') || 'The encounter'), compileReason: cause,
      } } : { reason, detail: {} };
  };
  // The time lane owns the one refusal. Mute only the synchronous compile probe,
  // never other actions while an adoption promise is pending.
  const readyBase = async (keys: readonly string[]): Promise<boolean> => {
    if (await window.ftAwaitEntityBase?.(keys) === false) return false;
    return withQuietUnported(() => entityCompileReady(keys, false));
  };
  const ready = async (): Promise<boolean> => {
    if (!encounterId || !await readyBase([encounterId])) return false;
    return readyBase([encounterId, ...rosterOf(encounterFor(encounterId))
      .map(member => upper(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id')))]);
  };
  let timer: number | undefined;
  try {
    return await Promise.race([ready().then(ok => ok ? null : unavailable()), new Promise<TimeRowsRefusal>(done => {
      timer = window.setTimeout(() => done(unavailable('entity-base-timeout')), 20000);
    })]);
  } finally { if (timer !== undefined) window.clearTimeout(timer); }
}
/** The T1 readers over the held encounter (definitions, derived clock, connected-peer walk); null when an input is missing. */
function readersFor(encounter: Row | null): EndTurnReaders | null {
  if (!encounter) return null;
  try { return turnReaders(encounter); } catch { return null; }
}
const zoneExpiryInFlight = new Set<string>();
// A queued band owns its retry through pending debt while the held columns catch up.
const lightRoundHints = new Map<string, string>();
/** Only a complete, focused snapshot can exclude this encounter from new clock debt.
 * This says nothing about previously recorded spans or encounters nobody holds. */
function timeRoundDue(input: { campaignId: string; encounterId?: string; onCheckpoint?: (settle: (roundRecorded: boolean) => void) => void }): boolean {
  const campaignId = upper(input.campaignId), campaign = campaignFor(campaignId);
  const id = upper(input.encounterId || _ftCamField(campaign, 'encounter_loaded'));
  if (!campaign || !id || id !== focusedEncounterId()) return true;
  const encounter = encounterFor(id);
  if (!encounter || !Array.isArray(encounter.encounter_creatures_arr)
    || typeof window.ftRequireEntityBase !== 'function'
    || !withQuietUnported(() => entityCompileReady([id, ...rosterOf(encounter).map(ecId)], false))) return true;
  const combat = inCombatOf(encounter);
  if (combat === null) return true;
  if (combat) return false;
  const read = readersFor(encounter); if (!read) return true;
  try {
    const light = lightForClock(campaign, derivedClockOf(campaign));
    const band = light.campaign_light + '|' + light.campaign_sun_state;
    if (light.campaign_light === _ftCamField(campaign, 'campaign_light')
      && light.campaign_sun_state === _ftCamField(campaign, 'campaign_sun_state')) lightRoundHints.delete(campaignId);
    else if (lightRoundHints.get(campaignId) !== band) {
      lightRoundHints.set(campaignId, band);
      input.onCheckpoint?.(recorded => {
        if (!recorded && lightRoundHints.get(campaignId) === band) lightRoundHints.delete(campaignId);
      });
      return true;
    }
    const roster = rosterOf(encounter);
    return roster.some(row => {
      if (statusRows(row, 'target').some(status => statusItemId(status) === STATUS_IDS.suffocating && !statusPaused(status))) return true;
      if (statusRows(row, 'target').some(status => statusItemId(status) === STATUS_IDS.resting && !statusPaused(status)
        && statusEndSeconds(status, read) !== null && statusEndSeconds(status, read)! <= read.nowSeconds)) return true;
      const future = new Set(statusRows(row, 'target').filter(status => {
        const end = statusEndSeconds(status, read);
        return end !== null && end > read.nowSeconds;
      }).map(status => upper(statusScalar(status, 'status_id'))));
      if (oocRoundDue(row, read) || calendarRoundDue(row, read, future)) return true;
      const charges = chargeTicksFor(row, read.abilityEntries(row), read);
      return charges.ticks.length > 0 || charges.gates.length > 0
        || Number(_ftCamField(campaign, 'waking_weariness_enabled') ?? 0) === 1
          && statusRows(row, 'target').some(s => statusItemId(s) === '5AA76DEE-CE52-E021-67CD-8C701DA0E56F');
    }) || calendarInventoryDue(encounter, roster, read)
      || (read.zoneStatusRows?.() ?? []).some(status => !statusPaused(status) && Number(status.status_aoe_source ?? 0) !== 0);
  } catch { return true; } // An unavailable definition/reader preserves debt; it never proves idleness.
}
async function expireObservedZones(campaignId: string, encounterId?: string): Promise<void> {
  if (window.ftBaseCommitsPaused?.()) return;
  if (window.FateTide?.user?.isGM !== true) return;
  const campaign = campaignFor(upper(campaignId));
  const context = (window as unknown as { getSidelogContext?: () => { encounter_id?: unknown } | null }).getSidelogContext?.();
  const id = upper(encounterId || context?.encounter_id || (campaign && ftClockCampaignField(campaign, 'encounter_loaded')));
  if (zoneExpiryInFlight.has(id)) return;
  const read = readersFor(encounterFor(id));
  if (!read) return;
  zoneExpiryInFlight.add(id);
  try { await commitDueZoneExpiries(id, read); } finally { zoneExpiryInFlight.delete(id); }
}
/** A PRIVATE retry snapshot from the endpoint's authoritative answer (a stale CAS): the shared campaign cache is never
 * written here — it follows the normal campaign_update path only (Codex review 2026-09-10). */
function snapshotOf(snapshot: { clock?: unknown; running_since?: unknown; server_utc_now?: unknown; campaign_light?: unknown; campaign_sun_state?: unknown } | null): { clock: CampaignClockCols; runningSince: string | null; serverNow: number | null; receivedAt: number; campaign_light: string | null; campaign_sun_state: string | null } | null {
  const clock = asRow(snapshot?.clock);
  if (!clock) return null;
  const cols = { year: Number(clock.year), month: Number(clock.month), day: Number(clock.day), hour: Number(clock.hour), minute: Number(clock.minute), second: Number(clock.second) };
  if (!Object.values(cols).every(Number.isInteger)) return null;
  return { clock: cols, runningSince: typeof snapshot?.running_since === 'string' ? snapshot.running_since : null,
    serverNow: ftClockParseUtc(typeof snapshot?.server_utc_now === 'string' ? snapshot.server_utc_now : null), receivedAt: performance.now(),
    campaign_light: typeof snapshot?.campaign_light === 'string' ? snapshot.campaign_light : null,
    campaign_sun_state: typeof snapshot?.campaign_sun_state === 'string' ? snapshot.campaign_sun_state : null };
}
function derivedSnapshotClock(campaign: Row, snapshot: NonNullable<ReturnType<typeof snapshotOf>>): CampaignClockCols {
  if (snapshot.serverNow === null) return snapshot.clock;
  const base = ftClockBaseFromArr(campaign);
  base.cols = snapshot.clock;
  base.runningSinceRaw = snapshot.runningSince;
  base.runningSinceMs = base.derived ? ftClockParseUtc(snapshot.runningSince) : null;
  return ftDeriveCampaignClock(base, snapshot.serverNow + Math.max(0, performance.now() - snapshot.receivedAt)).cols;
}
const emit = (name: string, detail: unknown): void => { window.dispatchEvent(new CustomEvent(name, { detail })); };

function lightForClock(campaign: Row, clock: CampaignClockCols) {
  return campaignLightForClock(campaign, clock);
}
const hungerSkipLogged = new Set<string>();

// ---- T4b/T4c: the frame every tab applies (status removals + the skills reversal + charges per touched row) ----
const applied = new Set<string>();
const toDeathsDelivered = new Set<string>();
const fortunateDelivered = new Set<string>();
function offerTimeFortunate(encounterId: string, operationId: string, offers: string[]): void {
  if (!offers.length || fortunateDelivered.has(operationId)) return;
  fortunateDelivered.add(operationId);
  if (fortunateDelivered.size > 256) fortunateDelivered.delete(fortunateDelivered.values().next().value!);
  try { offerFortunate(encounterId, offers[0]!, offers); } catch (error) { console.warn('time Fortunate offer:', error); }
}
export { setRowAbilityCharges } from '../janus-engine/ability/charges/charge-entry.js';
/** Upsert one lean status entry into the row's projections (target always — the entry targets this row; caster when self-cast). */
export function upsertStatusOnRow(row: Row, entry: Row): void {
  const sid = upper(entry.status_id); if (!sid) return;
  const statuses = (row.statuses ??= {}) as Row;
  const me = upper(asRow(row.encounter_creatures)?.encounter_creature_id);
  const caster = upper(entry.status_caster_id) || upper(entry.status_target_id);
  for (const mode of ['target', 'caster'] as const) {
    if (mode === 'caster' && caster !== me) continue;
    const list = Array.isArray(statuses[mode]) ? statuses[mode] as unknown[] : [];
    const i = list.findIndex(e => upper(asRow(e)?.status_id) === sid);
    if (i >= 0) list[i] = { ...(asRow(list[i]) ?? {}), ...entry }; else list.unshift({ ...entry }); // a NEW status first (PHP lists most-recently-touched first — N4e)
    statuses[mode] = list;
  }
}
function applyRowFrame(row: Row, part: TimePushRowFrame, definition?: (id: string) => Row | null): void {
  applyStatusFrame(row, part, definition);
  for (const c of part.charges ?? []) setRowAbilityCharges(row, upper(c.item_id), Number(c.current));
  if (part.meters) { const ec = (row.encounter_creatures ??= {}) as Row; for (const k of ['hunger_meter_current', 'thirst_meter_current'] as const) { const v = Number(part.meters[k]); if (v > 0) ec[k] = v; else delete ec[k]; } }
  for (const entry of part.statuses_upsert ?? []) {
    // R-122: the endpoint relays the page's definition shell as decoded JSON, so an empty layer map comes back as `[]` — read it as `{}`
    const shell = asRow(entry.status_arr);
    upsertStatusOnRow(row, shell && Array.isArray(shell.item_layer_types) && shell.item_layer_types.length === 0 ? { ...entry, status_arr: { ...shell, item_layer_types: {} } } : entry);
  }
  if (part.status_projection) {
    const projection = part.status_projection;
    if (Object.keys(projection.roots).length) applyStatusRootFields(row, projection.roots);
    const computed = (row.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
    const skills = (creature.skills ??= {}) as Row;
    for (const [key, patch] of Object.entries(projection.skills)) {
      const entry = (skills[key] ??= {}) as Row;
      for (const [field, value] of Object.entries(patch)) { if (value === null) delete entry[field]; else entry[field] = value; }
      if (!Object.keys(entry).length) delete skills[key];
    }
    const modifiers = (creature.modifiers ??= {}) as Row, sources = (creature.modifier_sources ??= {}) as Row;
    for (const [key, patch] of Object.entries(projection.globals)) {
      if (patch.value) modifiers[key] = patch.value; else delete modifiers[key];
      if (patch.sources.length) sources[key] = patch.sources; else delete sources[key];
    }
  }
}
function applyTimeFrame(encounterId: string, operationId: string, frame: { rows?: Record<string, TimePushRowFrame> } | null, tick?: TurnTickResult, hungerTick?: TurnTickResult): string[] {
  if ((!frame && !tick && !hungerTick) || applied.has(operationId)) return [];
  applied.add(operationId);
  if (applied.size > 256) applied.delete(applied.values().next().value!);
  const encounter = encounterFor(encounterId);
  const touched: string[] = [];
  const removedIds = new Set<string>();
  const upsertedIds = new Set<string>();
  const tickTouched = new Set(tick && encounter ? applyTurnTick(rosterOf(encounter), tick, id => readersFor(encounter)?.definition(id) ?? null, encounter) : []);
  if (tick && encounter) retireTickCreatures(encounterId, tick);
  for (const part of Object.values(frame?.rows ?? {})) for (const e of part?.statuses_upsert ?? []) upsertedIds.add(upper(e.status_id));
  for (const [actorId, part] of Object.entries(frame?.rows ?? {})) {
    const row = rowFor(encounter, actorId);
    if (!row || !part) continue;
    applyRowFrame(row, part, id => readersFor(encounter)?.definition(id) ?? null);
    // A row REMOVED and re-UPSERTED in the same frame (the statuses upsert revives a soft-deleted row under its old id — the
    // hourly hunger tick expiring and re-applying in one push) must survive the cross-projection sweep below.
    for (const id of part.status_remove_ids ?? []) if (!upsertedIds.has(upper(id))) removedIds.add(upper(id));
    touched.push(upper(actorId));
  }
  // (2026-09-28) the server interned each upserted layerless status's IDENTITY into that row's catalog (a rebuild does too): drop the
  // page's C1 carrier-catalog mirror for those rows so the next claim re-reads it instead of binding a stale catalog base
  const cataloged = Object.entries(frame?.rows ?? {}).filter(([, part]) => (part?.statuses_upsert ?? []).length > 0).map(([id]) => upper(id));
  try { if (cataloged.length) (window as unknown as { ftC1ForgetCarrierCatalogs?: (ids: unknown[]) => void }).ftC1ForgetCarrierCatalogs?.(cataloged); } catch { /* optional */ }
  // A cross-cast status lives in TWO projections (the target's and the caster's `statuses.caster`): drop it from every held
  // row that still carries it (Codex review 2026-09-10 #8), then re-render each row that changed.
  for (const row of rosterOf(encounter)) {
    const id = upper(asRow(row.encounter_creatures)?.encounter_creature_id);
    let changed = touched.includes(id) || tickTouched.has(id);
    for (const sid of removedIds) if (removeStatusFromRow(row, sid)) changed = true;
    if (changed && !touched.includes(id)) touched.push(id);
  }
  if (hungerTick && encounter) {
    for (const id of applyTurnTick(rosterOf(encounter), hungerTick, id => readersFor(encounter)?.definition(id) ?? null, encounter))
      if (!touched.includes(id)) touched.push(id);
    retireTickCreatures(encounterId, hungerTick);
  }
  for (const id of touched) { const row = rowFor(encounter, id); if (row) handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true }); }
  return touched;
}
/** Patch the held campaign columns and queue hints without replacing its blob or requesting a resync. */
const appliedCampaignClocks = new Set<string>();
function applyTimeCampaignDelta(campaignId: string, delta: Row | null, closed: unknown[] = [], operationId = ''): void {
  const campaign = campaignFor(campaignId);
  if (!campaign) return;
  const computed = (campaign.computed ??= {}) as Row, state = (computed.campaign ??= {}) as Row;
  if (Array.isArray(delta?.pending)) {
    const beat = (state.heartbeat ??= {}) as Row;
    beat.pending = delta.pending;
  }
  const heartbeat = asRow(state.heartbeat);
  if (closed.length) {
    const ids = new Set(closed.map(upper));
    if (heartbeat && Array.isArray(heartbeat.pending)) heartbeat.pending = heartbeat.pending.filter(entry => !ids.has(upper(asRow(entry)?.id)));
    for (const id of ids) pendingDone.add(id);
    pendingListed = pendingListed.filter(entry => !ids.has(upper(entry.id)));
  }
  const columns = asRow(delta?.columns);
  if (!columns) return;
  const key = campaignId + ':' + operationId;
  if (operationId && appliedCampaignClocks.has(key)) return;
  if (operationId) { appliedCampaignClocks.add(key); if (appliedCampaignClocks.size > 256) appliedCampaignClocks.delete(appliedCampaignClocks.values().next().value!); }
  const beforeLight = _ftCamField(campaign, 'campaign_light'), beforeSun = _ftCamField(campaign, 'campaign_sun_state');
  const bucket = (campaign.campaigns ??= {}) as Row;
  for (const [key, value] of Object.entries(columns)) bucket[key] = value;
  if (delta && 'sun_transition_minutes' in delta) state.sun_transition_minutes = delta.sun_transition_minutes;
  if (typeof delta?.server_utc_now === 'string') state.server_utc_now = delta.server_utc_now;
  fallbackClockAnchorOf(campaign, true);
  (window as unknown as { ftCampaignClock?: { captureBlob?: (row: Row) => void } }).ftCampaignClock?.captureBlob?.(campaign);
  if (typeof getCampaignTimeSummary === 'function') void getCampaignTimeSummary(campaign as unknown as Parameters<typeof getCampaignTimeSummary>[0]);
  (window as unknown as { ftUpdateActivityBars?: () => void }).ftUpdateActivityBars?.();
  if ((beforeLight !== _ftCamField(campaign, 'campaign_light') || beforeSun !== _ftCamField(campaign, 'campaign_sun_state'))
    && typeof ftSyncViewerAmbience === 'function') void ftSyncViewerAmbience(campaign);
  // Existing clock/activity/attendance listeners receive the same held object, without a fetch or replacement.
  emit('ft:campaign-state-received', { campaignId, campaignArr: campaign, delta: true });
  emit('ft:time-campaign-delta', { campaignId, columns, closed });
}
/** Inbound `janus_time_truth` frame (router.ts): a committed T4b push from ANY tab — apply its removals once. */
export function handleTimeTruth(command: { schema: string; operation_id: string; encounter_id: string; committed?: boolean; frame?: unknown; tick?: TurnTickResult; hunger_tick?: TurnTickResult; campaign_id?: string; campaign_delta?: unknown; pending_closed?: unknown[] }): void {
  if (!command || command.schema !== TIME_PUSH_SCHEMA || command.committed !== true) return;
  if (command.campaign_delta || command.pending_closed?.length) applyTimeCampaignDelta(upper(command.campaign_id), asRow(command.campaign_delta), command.pending_closed, upper(command.operation_id));
  const encounterId = upper(command.encounter_id), operationId = upper(command.operation_id);
  if (!encounterId || !operationId) return;
  const touched = applyTimeFrame(encounterId, operationId, asRow(command.frame) as { rows?: Record<string, TimePushRowFrame> } | null, command.tick, command.hunger_tick);
  emit('ft:time-truth-frame', { command, touched });
}

type PostOutcome = { kind: 'ok'; result: Row } | { kind: 'stale'; authoritative: Row | null; result: Row } | { kind: 'refused'; status: number; error: string; result: Row | null } | { kind: 'unknown'; error: string };
async function post(command: TimePushCommand): Promise<PostOutcome> {
  markStall('time-truth:post');
  withLogParticipants(command);
  let response: Response;
  try {
    response = await saveAppliedTruth(ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withLogParticipants(command)), signal: AbortSignal.timeout(20000) });
  } catch (error) { return { kind: 'unknown', error: String(error instanceof Error ? error.message : error) }; }
  let result: Row | null = null;
  try { result = asRow(await response.json()); } catch { result = null; }
  // Older membership conflicts share the time lane's correction disposition.
  // An answer without targeted saved inputs receives a named snapshot refusal.
  if (response.status === 409 && result?.error === 'held-row-not-a-member') {
    const stale = { kind: 'stale' as const, authoritative: asRow(result.authoritative), result };
    return stale;
  }
  if (response.ok && result?.ok === true) return { kind: 'ok', result };
  if (response.status === 409 && result?.error === 'stale') return { kind: 'stale', authoritative: asRow(result.authoritative), result };
  if (response.status >= 400 && response.status < 500) return { kind: 'refused', status: response.status, error: String(result?.error || response.statusText || 'http-' + response.status), result };
  return { kind: 'unknown', error: String(result?.error ?? response.statusText ?? 'no answer') };
}

/** Hold missing catalog definitions and trigger payloads; the encounter and member compiles are already held. */
const HUNGER_TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];
async function hydrateHungerDefinitions(encounter: Row | null): Promise<void> {
  const readers = readersFor(encounter);
  if (!encounter || !readers) return;
  const want = new Set<string>();
  for (const row of rosterOf(encounter)) {
    const ec = asRow(row.encounter_creatures);
    const type = String(scalar(asRow(row.items)?.item_type) ?? scalar(ec?.item_type) ?? '').toLowerCase();
    if (type !== 'player_character' || Number(scalar(ec?.encounter_creature_dead) ?? 0) !== 0) continue;
    const abilities = asRow(asRow(asRow(row.computed)?.creature)?.abilities);
    for (const carrier of HUNGER_TRIGGER_CARRIERS) {
      for (const entry of [...readers.entries(row, carrier), ...(Array.isArray(row[carrier]) ? row[carrier] as unknown[] : []), ...(Array.isArray(abilities?.[carrier]) ? abilities![carrier] as unknown[] : [])]) {
        const e = asRow(entry), nested = asRow(e?.item_arr);
        if (e?.passive_conditions_active === false || (nested && asRow(nested.item_layer_types))) continue;
        const id = upper(layerListEntryUuid(nested ?? entry) || e?.ability_id);
        if (/^[0-9A-F-]{36}$/.test(id) && !readers.definition(id)) want.add(id);
      }
    }
    for (const id of [...HUNGER_APPLY_IDS, HUNGER_IDS.tick]) if (!readers.definition(id)) want.add(id);
    for (const status of statusRows(row, 'target')) { const id = statusItemId(status); if (!readers.definition(id)) want.add(id); }
    if (AIRBORNE_SPEED_IDS.has(upper(ecLean(row, 'preferred_speed_type')))) {
      for (const id of ['BE42824F-46BE-EC80-AB7F-B823190AB6C8', 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA']) if (!readers.definition(id)) want.add(id);
    }
  }
  await Promise.all([...want].slice(0, 48).map(async id => {
    try { await fetchAndCacheObject(id); } catch { /* unheld: the resolver withholds */ }
  }));
  const pcs = rosterOf(encounter).filter(row => String(itemField(row, 'item_type') ?? ecLean(row, 'item_type')) === 'player_character'
    && !Number(ecLean(row, 'encounter_creature_dead')));
  if (pcs.length) await hydrateTurnTriggerDefinitions(pcs[0]!, encounter,
    [...HUNGER_APPLY_IDS, 'BE42824F-46BE-EC80-AB7F-B823190AB6C8'].map(id => readers.definition(id)).filter((def): def is Row => !!def), pcs);
}

export type TimePushOutcome = 'handled' | 'unresolved' | null;
type TimePushRequest = { campaignId: string; encounterId: string; rounds: number; fastForward?: boolean; rollPrivate?: number; gmLog?: boolean; logSeconds?: number; hungerOnly?: boolean; label?: string; onRefused?: (reason: string) => void; onCommittedClock?: (clock: CampaignClockCols) => void };
const activePushes = new Map<string, Promise<TimePushOutcome>>();
// A cadence must not manufacture another operation for the same refused state.
// New held encounter state or an explicit GM action can decide new work.
const terminalPushes = new Map<string, { operationId: string; encounterId: string; encounter: Row | null; version: unknown }>();
function timePushBlocked(campaignId: string): boolean {
  const terminal = terminalPushes.get(campaignId);
  if (!terminal) return false;
  const encounter = encounterFor(terminal.encounterId);
  if (encounter !== terminal.encounter || encounter?._ws_version !== terminal.version) {
    terminalPushes.delete(campaignId);
    return false;
  }
  return true;
}
// A changed roster can invalidate either a row CAS or its persisted governing owner.
// Refresh once and replan; a repeated refusal retains its original name and status.
const TIME_TARGET_OWNER_CONFLICT = 'Target row outside this governing owner';
const REFRESHABLE_TIME_CONFLICTS = new Set(['stale', 'meter-stale', 'removal-row-missing', 'removal-row-differs', 'hunger-zero-row-stale', TIME_TARGET_OWNER_CONFLICT]);
function refuseTimePush(input: TimePushRequest, command: TimePushCommand, error: string, status: number, result: Row | null): null {
  const campaignId = upper(input.campaignId), encounterId = upper(input.encounterId);
  if (terminalPushes.get(campaignId)?.operationId === command.operation_id) return reportTimePushRefusal(input, 'operation-terminal');
  unresolvedPushes.delete(campaignId);
  const encounter = encounterFor(encounterId);
  terminalPushes.set(campaignId, { operationId: command.operation_id, encounterId, encounter, version: encounter?._ws_version });
  const info = { error, status, terminal: true, result,
    conflict: { response: { error: String(result?.error ?? error), detail: String(result?.why ?? '') } } };
  emit('ft:time-truth-refused', { command, info });
  if (error === 'member-time-pending') return reportMemberTimePending(input,
    (Array.isArray(result?.pending) ? result.pending as PendingEntry[] : pendingHints(campaignId, encounterId))
      .find(entry => entry.kind === MEMBER_TIME_KIND));
  return reportTimePushRefusal(input, error, { operationId: command.operation_id, ...info });
}
// An ambiguous answer retains the already-decided command. Recovery never
// invokes the resolver/rolls again, and each attempt makes at most two identical posts.
const unresolvedPushes = new Map<string, () => Promise<TimePushOutcome>>();
const recoveringPushes = new Set<string>();
export const hasUnresolvedTimePush = (campaignId: string): boolean => unresolvedPushes.has(upper(campaignId));
export async function retryUnresolvedTimePush(campaignId: string): Promise<TimePushOutcome> {
  const id = upper(campaignId), retry = unresolvedPushes.get(id);
  if (!retry) return null;
  if (recoveringPushes.has(id)) return 'unresolved';
  recoveringPushes.add(id);
  try { return await retry(); } finally { recoveringPushes.delete(id); }
}
export async function tryCommitTimePush(input: TimePushRequest): Promise<TimePushOutcome> {
  const id = upper(input.campaignId);
  if (input.hungerOnly && timePushBlocked(id)) return null;
  const active = activePushes.get(id);
  if (active) return input.hungerOnly ? null : active;
  if (!input.hungerOnly) terminalPushes.delete(id);
  const task = commitTimePush(input);
  activePushes.set(id, task);
  try { return await task; }
  catch (error) { return reportTimePushRefusal(input, 'planning-failed', { error: String(error instanceof Error ? error.message : error) }); }
  finally { if (activePushes.get(id) === task) activePushes.delete(id); }
}
async function commitTimePush(input: TimePushRequest, recovery?: { operationId: string; snapshot: ReturnType<typeof snapshotOf> }, flow = { refreshed: false }): Promise<TimePushOutcome> {
  const campaignId = upper(input.campaignId), encounterId = upper(input.encounterId);
  markStall('time-truth:commit');
  if (unresolvedPushes.has(campaignId)) return await retryUnresolvedTimePush(campaignId);
  const held = encounterFor(encounterId), read = readersFor(held);
  const chunk = Math.max(1, Math.min(128, Math.floor(32768 / (64 * Math.max(1, rosterOf(held).length)))));
  if (!recovery && Number.isInteger(input.rounds) && input.rounds <= TIME_PUSH_MAX_ROUNDS && !input.fastForward && !input.hungerOnly && input.rounds > chunk && held && !rosterOf(held).some(row => row._entity_refusal) && inCombatOf(held) === false && read
    && rosterOf(held).some(row => oocRoundDue(row, read))) {
    let remaining = input.rounds;
    const pushChunks = async (retry?: () => Promise<TimePushOutcome>): Promise<TimePushOutcome> => {
      while (remaining > 0) {
        const rounds = Math.min(chunk, remaining), last = remaining === rounds;
        const outcome = await (retry ? retry() : commitTimePush({ ...input, rounds, gmLog: last && input.gmLog !== false,
          logSeconds: input.logSeconds ?? input.rounds * 6 }, undefined, flow));
        retry = undefined;
        if (outcome === 'unresolved') {
          // The child owns its decided payload (including any hunger/rest retry).
          // Retain the outer debt too, on every interruption, without deciding it again.
          const resume = unresolvedPushes.get(campaignId);
          if (resume) unresolvedPushes.set(campaignId, () => pushChunks(resume));
          return outcome;
        }
        if (outcome !== 'handled') {
          if (remaining < input.rounds) emit('ft:time-truth-partial', { info: { error: 'time-push:partial', terminal: true,
            campaignId, encounterId, requested_rounds: input.rounds, committed_rounds: input.rounds - remaining, remaining_rounds: remaining } });
          return outcome;
        }
        remaining -= rounds;
      }
      return 'handled';
    };
    return await pushChunks();
  }
  const campaign = campaignFor(campaignId);
  const slice = input.hungerOnly === true ? 'hunger-heartbeat' : 'time-push';
  if (!campaign) { emit('ft:time-slice-unavailable', { reason: 'campaign-arr', slice }); return reportTimePushRefusal(input, 'campaign-arr'); }
  let encounter = encounterFor(encounterId);
  const readiness = await awaitTimeActionRows(encounterId);
  encounter = encounterFor(encounterId);
  const deferred = rosterOf(encounter).filter(row => row._entity_refusal && asRow(row._time_source));
  if (!inCombatOf(encounter) && !deferred.length && pendingHints(campaignId, encounterId).some(entry => entry.kind === MEMBER_TIME_KIND)) {
    await drainPendingTimeEvents(campaignId);
    if (pendingHints(campaignId, encounterId).some(entry => entry.kind === MEMBER_TIME_KIND))
      return reportMemberTimePending(input, pendingHints(campaignId, encounterId).find(entry => entry.kind === MEMBER_TIME_KIND)!);
  }
  let memberQueueBefore: unknown = null;
  if (readiness && (!readiness.reason.startsWith('entity-compile-refused:') || !deferred.length || !encounter
    || !withQuietUnported(() => entityCompileReady([encounterId, ...rosterOf(encounter).filter(row => !deferred.includes(row)).map(ecId)], false))))
    return reportTimePushRefusal(input, readiness.reason, readiness.detail);
  if (deferred.length && readiness) {
    const deferredRead = readersFor(encounter);
    const cause = input.fastForward ? 'fast-forward' : runningSinceOf(campaign) ? 'running-clock' : input.hungerOnly || hungerEnabledOf(campaign) ? 'hunger'
      : Number(_ftCamField(campaign, 'waking_weariness_enabled') ?? 0) === 1 ? 'weariness'
      : inCombatOf(encounter) ? 'combat' : holdsTaggedSummon(rosterOf(encounter)) ? 'summons'
      : !deferredRead ? 'readers-unavailable' : memberTimeRefusal(encounter!, rosterOf(encounter), deferredRead);
    if (cause) return reportTimePushRefusal(input, readiness.reason, { ...readiness.detail, debtReason: cause });
    const listed = await postPending({ mode: 'pending_list', encounter_id: encounterId });
    if (listed?.status !== 200 || !Array.isArray(listed.result?.pending)) return reportTimePushRefusal(input, 'member-time-queue-unavailable');
    if (listed.result.member_time_protocol !== 1) return reportTimePushRefusal(input, 'member-time-protocol-unavailable');
    const debt = listed.result.pending.find((entry: PendingEntry) => entry.kind === MEMBER_TIME_KIND) as Row | undefined;
    if (debt) {
      const intervals = debt.member_intervals as MemberTimeInterval[];
      if (intervals.some(interval => !deferred.some(row => ecId(row) === interval.member_id))) return reportMemberTimePending(input, debt);
      memberQueueBefore = [debt.id, Number(debt.coalesced ?? 0)];
    }
    encounter = memberTimeScene(encounter!, rosterOf(encounter).filter(row => !deferred.includes(row)));
  }
  const heartbeatRaw = asRow(asRow(asRow(campaign.computed)?.campaign)?.heartbeat);
  const deriveHeartbeat = (window as unknown as { ftRefreshCreatureHeartbeat?: (row: Row) => Row }).ftRefreshCreatureHeartbeat;
  const heartbeat = heartbeatRaw && deriveHeartbeat ? deriveHeartbeat(heartbeatRaw) : heartbeatRaw;
  if (hungerEnabledOf(campaign)) { await hydrateHungerDefinitions(encounter); encounter = encounterFor(encounterId); }
  const [, calendarFacts] = await Promise.all([
    encounter && !input.hungerOnly && !inCombatOf(encounter)
      ? Promise.all(rosterOf(encounter).map(row => prefetchTickDefinitions(encounter!, row))) : undefined,
    encounter && !input.hungerOnly && !inCombatOf(encounter) ? calendarFactsFor(encounterId) : undefined,
  ]);
  const deathResources = encounter && !input.hungerOnly && !inCombatOf(encounter)
    ? await prepareRoundDeaths(encounter, campaign) : undefined;
  const roundReaders = (scene: Row, clock: CampaignClockCols): EndTurnReaders => {
    const read = calendarReaders(scene, campaign, clock, consent);
    if (read.tick && deathResources) read.tick.ability = { ...read.tick.ability, deathResources };
    return read;
  };
  let withheldHunger: Record<string, string> = {};
  let consent = createTriggerConsent(encounterId);
  const resolve = async (snapshot: ReturnType<typeof snapshotOf>, preflight = true): Promise<ReturnType<typeof resolveTimePush>> => { snapshot ??= recovery?.snapshot ?? null; try { emit('ft:time-native-plan', { campaignId, encounterId, preflight }); return await resolveTimePushAsync({
    campaignId, encounterId, rounds: Number(input.rounds), fastForward: input.fastForward === true, rollPrivate: input.rollPrivate ?? 2, gmLog: input.gmLog !== false,
    isGM: window.FateTide?.user?.isGM === true, inCombat: inCombatOf(encounter),
    clock: snapshot?.clock ?? (ftClockColsFromArr(campaign) as CampaignClockCols), runningSince: snapshot ? snapshot.runningSince : runningSinceOf(campaign), calendar: ftClockCalendarFromArr(campaign),
    heartbeat: heartbeat as never, rows: rosterOf(encounter), encounter: encounter ?? undefined,
    readers: encounter ? calendarReaders(encounter, campaign, derivedClockOf(campaign), consent) : null, hungerEnabled: hungerEnabledOf(campaign),
    roundReaders, roundPreflightOnly: preflight, calendarFacts, weariness: Number(_ftCamField(campaign, 'waking_weariness_enabled') ?? 0) === 1,
    derivedClock: snapshot ? derivedSnapshotClock(campaign, snapshot) : derivedClockOf(campaign),
    hungerOnly: input.hungerOnly === true, withheldHunger,
  }, () => crypto.randomUUID().toUpperCase()); } catch (error) {
    if (error instanceof HandBack && error.reason.includes('trigger-consent')) return { supported: false, reason: error.reason };
    throw error;
  } };
  let scope = await planWithConsent(encounterId, activeActor(encounter), c => { consent = c; return resolve(null); }, s => !s.supported && s.reason.includes('trigger-consent'));
  // R-100 (lane A4, Codex P2): an owed "to death" cast has no PHP fallback, so PROVE it before the push commits — a dry run of the ability lane
  // on the page rows (the PC already holds Exhausted, the cast's own gate). A hand-back WITHHOLDS that PC's hunger step (decided again on the
  // next push / heartbeat), never a committed push with a lost contest. A PC that gains Exhausted in THIS push (both meters at zero) cannot be
  // proven on the current rows: its cast is tried after the commit (named residual on R-100).
  if (scope.supported && scope.command.to_deaths?.length) {
    const dryRun = (window as unknown as { ftDryRunAbilityUse?: (i: { encounterId: string; actorId: string; targetIds: string[]; abilityId: string; rollPrivate?: number }) => Promise<{ outcome: string; reason: string | null }> }).ftDryRunAbilityUse;
    for (const td of scope.command.to_deaths) {
      const actor = upper(td.actor_id);
      const held = asRow(rowFor(encounter, actor)?.statuses)?.target;
      if (!(Array.isArray(held) && held.some(s => upper(asRow(s)?.status_item_id) === HUNGER_IDS.exhausted))) continue;
      let verdict: { outcome: string; reason: string | null } | null = null;
      try { await fetchAndCacheObject(upper(td.ability_id)); verdict = typeof dryRun === 'function' ? await dryRun({ encounterId, actorId: actor, targetIds: [actor], abilityId: upper(td.ability_id), rollPrivate: input.rollPrivate ?? 2 }) : null; }
      catch { verdict = null; }
      if (!verdict || verdict.outcome === 'handback') withheldHunger[actor] = `to-death-handback:${actor}:${verdict?.reason ?? 'no-lane'}`;
    }
    if (Object.keys(withheldHunger).length) scope = await resolve(null);
  }
  if (!scope.supported) { emit('ft:time-slice-unavailable', { reason: scope.reason, detail: scope.detail ?? null, slice }); return reportTimePushRefusal(input, scope.reason, scope.detail); }
  if (scope.command.tick || scope.command.hunger_tick) scope = await resolve(null, false);
  if (!scope.supported) return reportTimePushRefusal(input, scope.reason, scope.detail);
  const fortunateOffers = [...(scope.command.tick?.fortunateOffers ?? []), ...(scope.command.hunger_tick?.fortunateOffers ?? [])];
  if (scope.command.tick) delete scope.command.tick.fortunateOffers; // page-only, exactly as the turn lane
  if (scope.command.hunger_tick) delete scope.command.hunger_tick.fortunateOffers;
  const prepare = (decided: TimePushCommand, snapshot: ReturnType<typeof snapshotOf> = null): TimePushCommand => {
    const beforeLight = snapshot ? snapshot.campaign_light : _ftCamField(campaign, 'campaign_light') || null;
    const beforeSun = snapshot ? snapshot.campaign_sun_state : _ftCamField(campaign, 'campaign_sun_state') || null;
    const light = lightForClock(campaign, decided.expected.clock);
    const rows = (decided.rows ?? []).filter(r => !String(r.rolls_full).startsWith('time-push:hunger') || !hungerSkipLogged.has(String(r.rolls_full)));
    if (beforeLight !== light.campaign_light || beforeSun !== light.campaign_sun_state) {
      // The native save carries both light columns and this transition row.
      // Encounter overrides stay put; perception and authored light/sun conditions read the new pair.
      if (light.message) rows.push({ roll_type_1: light.message, roll_type_2: null, roll_result: 0, roll_private: (input.rollPrivate ?? 2) as 0 | 1 | 2,
        roll_item_id: null, roll_by: null, roll_to: null, rolls_full: '' });
    }
    return { ...decided, ...(readiness && deferred.length ? { member_queue_before: memberQueueBefore, member_intervals: deferred.map(row => ({ member_id: ecId(row),
      from: decided.before.clock, to: decided.expected.clock, rounds: decided.seconds / 6, status_fingerprint: memberTimeStatusFingerprint(row) })) } : {}),
      ...(input.logSeconds !== undefined ? { log_seconds: input.logSeconds } : {}), before: { ...decided.before, campaign_light: beforeLight, campaign_sun_state: beforeSun },
      expected: { ...decided.expected, campaign_light: light.campaign_light, campaign_sun_state: light.campaign_sun_state, sun_transition_minutes: light.transitions }, rows };
  };
  let command = prepare(scope.command, recovery?.snapshot ?? null);
  if (recovery) command.operation_id = recovery.operationId;
  // The bridge accepts caster channels, so never use the encounter-id fallback
  // from activeActor for a clock push. A clock answer is applied only after saving.
  const actorId = activeActor(encounterFor(encounterId));
  if (scope.hunger_withheld?.length) { emit('ft:time-hunger-withheld', { campaignId, encounterId, reasons: scope.hunger_withheld, slice }); console.warn('time push: hunger withheld (board R-129)', scope.hunger_withheld); }
  // Ambiguous saves retain their exact payload for receipt recovery. A definite
  // refusal retires the operation; no outcome reaches a legacy rule endpoint.
  const unresolved = (why: string, info: Record<string, unknown> = {}): 'unresolved' => {
    unresolvedPushes.set(campaignId, () => send(false));
    emit('ft:time-truth-refused', { command, info: { error: why, terminal: false, unknown: true, ...info } });
    reportTimePushRefusal(input, why, info, true);
    return 'unresolved';
  };
  const send = async (allowRefresh: boolean): Promise<TimePushOutcome> => {
    let rejected: { error: string; status: number; result: Row | null } | null = null;
    try {
    if (terminalPushes.get(campaignId)?.operationId === command.operation_id) return reportTimePushRefusal(input, 'operation-terminal');
    let posts = recovery ? 1 : 0;
    for (;;) {
      emit('ft:time-truth-sending', { command });
      const outcome = await post(command);
      posts++;
      if (outcome.kind === 'stale' || outcome.kind === 'refused') rejected = { error: outcome.kind === 'stale' ? 'stale' : outcome.error,
        status: outcome.kind === 'stale' ? 409 : outcome.status, result: outcome.result };
      if (outcome.kind === 'ok') {
        unresolvedPushes.delete(campaignId);
        if (readiness && command.member_intervals?.length) for (const row of deferred) {
          const detail = participantCompileRefusal(held!, row, ecId(row));
          reportTimePushRefusal(input, 'entity-compile-refused:' + detail.rowRefusal,
            { actorId: ecId(row), memberName: detail.memberName, compileReason: detail.rowRefusal, deferred: true });
        }
        const savedClock = snapshotOf({ clock: outcome.result.clock });
        for (const r of command.rows ?? []) if (String(r.rolls_full).startsWith('time-push:hunger')) hungerSkipLogged.add(String(r.rolls_full));
        const campaignDelta = asRow(outcome.result.campaign_delta) ?? (savedClock ? {
            columns: { ...Object.fromEntries(Object.entries(savedClock.clock).map(([key, value]) => ['campaign_time_' + key, value])),
              campaign_clock_running_since: outcome.result.running_since ?? null,
              campaign_light: (Array.isArray(outcome.result.light) ? outcome.result.light[0] : command.expected.campaign_light) ?? null,
              campaign_sun_state: (Array.isArray(outcome.result.light) ? outcome.result.light[1] : command.expected.campaign_sun_state) ?? null },
            sun_transition_minutes: lightForClock(campaign, savedClock.clock).transitions, server_utc_now: outcome.result.server_utc_now,
          } : null);
        if (campaignDelta) {
          applyTimeCampaignDelta(campaignId, campaignDelta, [], command.operation_id);
          window.ftPushCampaignTruth?.({ type: 'janus_time_truth', campaign_id: campaignId,
            operation_id: `${command.operation_id}:campaign`, command: { schema: TIME_PUSH_SCHEMA,
              operation_id: `${command.operation_id}:campaign`, encounter_id: '', campaign_id: campaignId, committed: true,
              campaign_delta: campaignDelta } });
        }
        if (savedClock) {
          try { input.onCommittedClock?.(savedClock.clock); } catch { /* observer cannot change a saved outcome */ }
        }
        // T4b: the sender applies the SAVED frame (the endpoint's answer, never this request's) once; its own WS frame is a no-op.
        const touched = command.mode === 'calendar' ? applyTimeFrame(encounterId, command.operation_id, asRow(outcome.result.frame) as { rows?: Record<string, TimePushRowFrame> } | null, outcome.result.tick as TurnTickResult | undefined, outcome.result.hunger_tick as TurnTickResult | undefined) : [];
        if (window.ftPushAppliedTruth && rowFor(encounterFor(encounterId), actorId)) {
          // Native time deltas already carry the saved statuses/resources/ticks.
          // The transport envelope adds no snapshot and owns this saved operation once.
          const applied_frame = buildAppliedFrame({ operation_id: command.operation_id, encounter_id: encounterId, actor_id: actorId,
            rev: 0, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] }, Infinity);
          window.ftPushAppliedTruth({ type: 'janus_time_truth', applied_frame, command: { ...command, actor_id: actorId, committed: true,
            campaign_delta: campaignDelta, frame: outcome.result.frame, tick: outcome.result.tick, hunger_tick: outcome.result.hunger_tick } });
        } else emit('ft:time-truth-no-push-actor', { encounterId, operationId: command.operation_id });
        emit('ft:time-truth-applied', { command, result: outcome.result, touched });
        offerTimeFortunate(encounterId, command.operation_id, fortunateOffers);
        void expireObservedZones(campaignId, encounterId).catch(error => console.warn('zone expiry:', error));
        window.dispatchEvent(new CustomEvent('ft:timed-finish-now', { detail: { encounterId, push: true, clock: outcome.result.clock } }));
        // T4f: the "to death" casts this push OWES (the server's own list, in legacy's order: hunger, then thirst), after the commit
        const owed = (Array.isArray(outcome.result.to_deaths) ? outcome.result.to_deaths as unknown[] : []).map(asRow).filter((r): r is Row => !!r);
        if (!toDeathsDelivered.has(command.operation_id)) {
          // Mark before dispatch: an exception after a roll is not authority to roll it again.
          toDeathsDelivered.add(command.operation_id);
          if (toDeathsDelivered.size > 256) toDeathsDelivered.delete(toDeathsDelivered.values().next().value!);
          for (const td of owed) await castToDeath(td, encounterId, command.roll_private);
        }
        const completeRests = (window as unknown as { ftCompleteDueLongRests?: (input: { campaignId: string; encounterId: string; clock: CampaignClockCols; rollPrivate: number }) => Promise<'handled' | 'refused' | 'unresolved'> }).ftCompleteDueLongRests;
        if (command.mode === 'calendar' && savedClock && !input.hungerOnly && completeRests) {
          const rested = await completeRests({ campaignId, encounterId, clock: savedClock.clock, rollPrivate: command.roll_private });
          if (rested === 'unresolved') return unresolved('long-rest-completion-unconfirmed');
        }
        if (command.mode === 'calendar' && savedClock && !input.hungerOnly)
          await settleCrafts({ encounterId, clock: savedClock.clock });
        return 'handled';
      }
      const conflict = outcome.kind === 'stale' ? 'stale' : outcome.kind === 'refused'
        && (outcome.status === 409 || outcome.status === 403 && outcome.error === TIME_TARGET_OWNER_CONFLICT) ? outcome.error : null;
      if ((outcome.kind === 'stale' || outcome.kind === 'refused') && conflict && REFRESHABLE_TIME_CONFLICTS.has(conflict) && allowRefresh && !command.member_intervals?.length && !flow.refreshed && posts < 2) {
        // A rejected CAS wrote nothing. Patch only its named rows and membership
        // difference, then decide once against those rows and the saved clock.
        flow.refreshed = true;
        const authoritative = asRow(outcome.kind === 'stale' ? outcome.authoritative : outcome.result?.authoritative);
        const snapshot = snapshotOf(authoritative);
        const fresh = await refreshTimeConflict(encounterId, authoritative, command.operation_id);
        emit('ft:time-conflict-recovery', { encounterId, operationId: command.operation_id, conflict, result: outcome.result, outcome: fresh ?? 'ready' });
        if (fresh === null) {
          // An idle clock command has no dice or effects to decide again. After the
          // row correction, prove that the same roster still owes no such work.
          const scene = encounterFor(encounterId), members = rosterOf(scene);
          const retryRead = scene ? readersFor(scene) : null;
          const clockOnly = conflict === 'stale' && snapshot && !outcome.result?.why
            && inCombatOf(scene) === command.before.in_combat
            && !command.tick && !command.hunger_tick && !command.round_debt
            && !command.removals?.length && !command.ticks?.length && !command.meters?.length
            && !command.thresholds?.length && !command.zeros?.length && !command.to_deaths?.length && !command.reapplies?.length
            && !Object.keys(command.frame?.rows ?? {}).length
            && (command.rows ?? []).every(row => ['the_sun_begins_to_rise', 'the_sun_has_risen', 'the_sun_begins_to_set', 'the_sun_has_set'].includes(row.roll_type_1))
            && !command.time_save?.writes.length && !command.time_save?.rows.length
            && !hungerEnabledOf(campaign) && Number(_ftCamField(campaign, 'waking_weariness_enabled') ?? 0) === 0
            && members.length === command.held_actor_ids?.length
            && members.every(row => command.held_actor_ids.includes(ecId(row)))
            && retryRead && !(retryRead.zoneStatusRows?.() ?? []).length
            && !members.some(row => statusRows(row, 'target').length || statusRows(row, 'caster').length
              || oocRoundDue(row, retryRead) || calendarRoundDue(row, retryRead)
              || (() => { const charges = chargeTicksFor(row, retryRead.abilityEntries(row), retryRead); return charges.ticks.length || charges.gates.length; })())
            && !calendarInventoryDue(scene!, members, retryRead);
          if (clockOnly && snapshot) {
            const now = derivedSnapshotClock(campaign, snapshot);
            command = prepare({ ...command,
              before: { ...command.before, clock: snapshot.clock, running_since: snapshot.runningSince },
              expected: { clock: ftCampaignTimeAddSeconds(now, ftClockCalendarFromArr(campaign), command.seconds) }, rows: [],
            }, snapshot);
            continue; // Same operation, at most two posts; no second resolver or roll.
          }
          const retried = await commitTimePush(input, { operationId: command.operation_id, snapshot }, flow);
          // The refreshed plan owns its named refusal as well as its successful save.
          return retried;
        }
        return refuseTimePush(input, command, fresh.endsWith('-timeout') || fresh === 'recovery-snapshot-unavailable' ? fresh : conflict,
          outcome.kind === 'refused' ? outcome.status : 409, { ...outcome.result, recovery_error: fresh });
      }
      if (outcome.kind === 'unknown' && posts < 2) continue; // same id: the durable receipt answers if it landed
      if (outcome.kind === 'unknown') return unresolved(outcome.error);
      const error = outcome.kind === 'stale' ? 'stale' : /operation id reused/i.test(outcome.error) ? 'time-push:receipt-conflict' : outcome.error;
      return refuseTimePush(input, command, error, outcome.kind === 'refused' ? outcome.status : 409, outcome.result);
    }
  } catch (error) {
    if (terminalPushes.get(campaignId)?.operationId === command.operation_id) return reportTimePushRefusal(input, 'operation-terminal');
    if (rejected) return refuseTimePush(input, command, rejected.error, rejected.status, rejected.result);
    return unresolved(String(error instanceof Error ? error.message : error)); // a post-dispatch exception is not proof of non-commit
  } };
  return await send(true);
}
/** R-123 (2026-10-01, lane N1b): the HUNGER HEARTBEAT — hunger / thirst are decided in this browser for every passage of time. The legacy
 * sweep (push_time_out_of_combat_status_effect_logic pass 3) no longer ticks them, so the GM page runs this on the running clock's
 * checkpoint cadence (campaign-clock-runtime.ts). Explicit push chunks include their own hunger decision. It is a
 * `rounds: 0` calendar command carrying ONLY the hunger family for the rows this page holds (scope ruling 2026-09-30: an encounter nobody
 * holds ticks nothing). Crafting also uses this cadence through its separate activity save; a failed craft never suppresses hunger.
 * A resolver gate writes no hunger effects. A terminal save refusal waits for fresh held state or an explicit GM action. */
let hungerHeartbeatInFlight = false;
/** (Codex review 2026-10-01) a heartbeat asked for while one is in flight is NOT dropped: the latest ask reruns once the active one ends
 *  (a paused clock has no periodic cadence to catch up a lost ask). */
let hungerHeartbeatTrailing: { campaignId: string; encounterId?: string } | null = null;
export async function tryCommitHungerHeartbeat(input: { campaignId: string; encounterId?: string }): Promise<TimePushOutcome> {
  if (window.ftBaseCommitsPaused?.()) return null;
  if (window.FateTide?.user?.isGM !== true) return null;
  if (timePushBlocked(upper(input.campaignId)) || activePushes.has(upper(input.campaignId))) return null;
  if (hasUnresolvedTimePush(input.campaignId)) return await retryUnresolvedTimePush(input.campaignId);
  if (hungerHeartbeatInFlight) { hungerHeartbeatTrailing = { ...input }; return null; }
  const campaignId = upper(input.campaignId);
  const campaign = campaignFor(campaignId);
  if (!campaign) return null;
  const encounterId = upper(input.encounterId || ftClockCampaignField(campaign, 'encounter_loaded'));
  if (!encounterId || !encounterFor(encounterId)) return null; // this page holds no encounter rows: nothing ticks
  hungerHeartbeatInFlight = true;
  try {
    if (await awaitTimeActionRows(encounterId)) return null;
    await expireObservedZones(campaignId, encounterId);
    const hunger = hungerEnabledOf(campaign)
      ? await tryCommitTimePush({ campaignId, encounterId, rounds: 0, rollPrivate: 2, gmLog: false, hungerOnly: true }) : null;
    if (inCombatOf(encounterFor(encounterId)) === false)
      await settleCrafts({ encounterId, clock: derivedClockOf(campaign) });
    return hunger;
  }
  finally {
    hungerHeartbeatInFlight = false;
    const trailing = hungerHeartbeatTrailing; hungerHeartbeatTrailing = null;
    if (trailing) window.setTimeout(() => { void tryCommitHungerHeartbeat(trailing).catch(() => { /* the next push / cadence decides again */ }); }, 0);
  }
}
/** T4f (2026-09-28): cast one owed "to death" ability — a Fortitude contest (Maimed on a failure), self-targeted by the PC — through the
 * client-truth ability lane (browser dice, Lane A's commit), AFTER the push committed. R-100 (lane A4, 2026-10-01): the lane serves the contest
 * (the elided-zero Fortitude net and the self-target contestee's own all-skill DIS were the hand-backs), so the endpoint's legacy
 * `to_death_cast` mode and `hunger_thirst_to_death_cast.php` are DELETED — a hand-back (nothing written) no longer reaches PHP: it is reported
 * (`outcome.handback`) and warned; the PC keeps its Starving / Parched re-apply and meets the contest again when that ends. A lane refusal is
 * legacy's silent no-op (its internal-rules precheck). Every outcome is emitted; a failed cast warns — the push itself is already committed. */
async function castToDeath(td: Row, encounterId: string, rollPrivate: number): Promise<void> {
  const actorId = upper(td.actor_id), abilityId = upper(td.ability_id), meter = String(td.meter ?? '');
  const via = 'ability-lane';
  let outcome: unknown = null;
  try {
    await fetchAndCacheObject(abilityId); // a system ability the PC does not hold — the resolver reads it from the object cache
    const lane = (window as unknown as { ftTryCommitAbilityUse?: (i: { encounterId: string; actorId: string; targetIds: string[]; abilityId: string; rollPrivate?: number }) => Promise<'handled' | 'refused' | null> }).ftTryCommitAbilityUse;
    outcome = typeof lane === 'function' ? await lane({ encounterId, actorId, targetIds: [actorId], abilityId, rollPrivate }) : null;
  } catch (error) {
    // (Codex review 2026-09-28) an exception is NOT proof nothing was written — report the unknown outcome, never cast again
    emit('ft:time-truth-to-death', { actorId, meter, via, outcome: { error: String(error instanceof Error ? error.message : error), unknown: true } });
    try { if (typeof displayWarning === 'function') displayWarning('A "to death" roll owed by the time push could not be confirmed — check the log before rolling it again.'); } catch { /* optional */ }
    return;
  }
  if (outcome === null) outcome = { handback: true }; // the lane named its reason on ft:action-slice-unavailable; nothing was written
  emit('ft:time-truth-to-death', { actorId, meter, via, outcome });
  if (asRow(outcome)?.handback === true) { try { if (typeof displayWarning === 'function') displayWarning('A "to death" roll owed by the time push could not be made in this browser.'); } catch { /* the warning never decides the outcome */ } }
}
/** READ-ONLY dry run: the resolver's verdict without a request — proves a fixture is in scope (or names the gate). */
function dryRunTimePush(input: { campaignId: string; encounterId: string; rounds: number }): Row {
  const campaignId = upper(input.campaignId), encounterId = upper(input.encounterId);
  const campaign = campaignFor(campaignId); const encounter = encounterFor(encounterId);
  if (!campaign) return { reason: 'campaign-arr' };
  const heartbeatRaw = asRow(asRow(asRow(campaign.computed)?.campaign)?.heartbeat);
  const deriveHeartbeat = (window as unknown as { ftRefreshCreatureHeartbeat?: (row: Row) => Row }).ftRefreshCreatureHeartbeat;
  const heartbeat = heartbeatRaw && deriveHeartbeat ? deriveHeartbeat(heartbeatRaw) : heartbeatRaw;
  const scope = resolveTimePush({ campaignId, encounterId, rounds: Number(input.rounds), rollPrivate: 2, gmLog: true, isGM: window.FateTide?.user?.isGM === true, inCombat: inCombatOf(encounter),
    clock: ftClockColsFromArr(campaign) as CampaignClockCols, runningSince: runningSinceOf(campaign), calendar: ftClockCalendarFromArr(campaign), heartbeat: heartbeat as never, rows: rosterOf(encounter), readers: readersFor(encounter), hungerEnabled: hungerEnabledOf(campaign), derivedClock: derivedClockOf(campaign), encounter: encounter ?? undefined,
    roundPreflightOnly: true, roundReaders: (scene, clock) => turnReaders(scene, clock) }, () => 'DRY-RUN');
  return scope.supported ? { in_scope: true, mode: scope.command.mode, removals: scope.command.removals.map(r => r.status_id), ticks: scope.command.ticks, meters: scope.command.meters, held: scope.command.held_actor_ids } : { reason: scope.reason, detail: scope.detail ?? null };
}
// ---- OFFLINE DELIVERY (Janus lane TIME, 2026-09-27) ----
// Work a browserless boundary recorded in the shared `pending_time_events` queue (the downtime cron's owed pass 1 for an
// encounter it could not run; see includes/functions/janus/encounter/pending_time_events.php) WAITS for an authorized browser:
// a focused GM or fallback member page is that browser. The campaign heartbeat embed lists the open rows (`computed.campaign.heartbeat.pending`); the
// tab claims one under a lease (`pending_claim`, one CAS UPDATE server-side) and resolves it (`pending_resolve`: the owed work
// and the row's closing commit together; `resolved_operation_id` is the durable receipt, so an unknown answer is retried ONCE
// with the SAME operation id and can never apply twice). A tab that dies between the two leaves a lease that simply expires;
// the next claimer reclaims it. Only kinds with a resolver here are drained — other lanes' kinds are left for their tabs.
const PENDING_ENDPOINT = '/options/game/actions/calendar_time_commit_inc.php';
const PENDING_RESOLVABLE_KINDS = new Set(['encounter_pass1', 'checkpoint_heartbeat_stalled', MEMBER_TIME_KIND]);
const PENDING_POLL_MS = 5000;
// Lane A8 (2026-10-02, playtest load finding: the GM tab posted time_truth_commit ~3.6×/min — this list poll, every GM tab, every 30 s, even
// with nothing owed): the campaign blob's heartbeat embed already lists every open row and is drained the moment it arrives
// (ft:campaign-state-received), so the server list is only the BACKSTOP for a paused campaign that never rebuilds — 5 minutes is enough.
const PENDING_LIST_MS = 300000;
let pendingListedAt = 0;
let pendingListedEncounter = '';
/** Last scheduling inventory, retained as a wake-up hint between current locked reads. */
let pendingListed: PendingEntry[] = [];
const pendingDone = new Set<string>();
const pendingClaims = new Map<string, Row>();
const refusedMemberDebts = new Map<string, string>();
const pendingBackoffUntil = new Map<string, number>();
const pendingFailures = new Map<string, { delay: number; state: string }>();
function backoffPending(entry: PendingEntry | string, stage: string, status: number, result: Row | null, operationId?: string): void {
  const id = typeof entry === 'string' ? entry : upper(entry.id), previous = pendingFailures.get(id);
  const delay = Math.min(60000, previous ? previous.delay * 2 : PENDING_POLL_MS);
  const state = JSON.stringify([stage, status, result?.error ?? null]);
  pendingFailures.set(id, { delay, state });
  pendingBackoffUntil.set(id, Date.now() + delay);
  if (previous?.state !== state) {
    emit('ft:time-pending-refused', { event: typeof entry === 'string' ? undefined : entry, stage, status, result, operation_id: operationId });
  }
}
let pendingDraining = false;
interface PendingEntry { id: string; kind: string; encounter_id: string | null; status: string; lease_live: boolean; lease_until?: number; finishing?: boolean; claimed_by?: string | null; reasons?: string[] }
type PendingLocks = { request: (name: string, opts: { ifAvailable: boolean }, cb: (lock: unknown) => Promise<void>) => Promise<void> };
let pendingLeader: { release: () => void } | null = null;
let pendingLeaderMounted = false;
const PENDING_GM_GRACE_MS = 120000;
const pendingSeenAt = new Map<string, number>();
const pendingFocusSince = new Map<string, number>();
const pendingProgressAt = new Map<string, number>();
// The existing committed frame is held evidence that a browser is settling this
// encounter. It suppresses discovery reads, never a known row's grace/backoff.
if (typeof window.addEventListener === 'function') window.addEventListener('ft:time-truth-frame', event => {
  const command = asRow(asRow((event as CustomEvent).detail)?.command);
  if (command?.committed === true && upper(command.encounter_id)) pendingProgressAt.set(upper(command.encounter_id), Date.now());
});
// Bound catch-up bursts to the normal two 30-second checkpoint spans/minute.
// Scale with the native chunk size and reserve a third five-round span of
// catch-up capacity: even a large roster can retire old debt while time runs.
// Finish acknowledgment is immediate; failed attempts use identity backoff.
const pendingChunkTimes = new Map<string, number[]>();
function canPlanPendingChunk(focused: string): boolean {
  const recent = (pendingChunkTimes.get(focused) ?? []).filter(at => Date.now() - at < 60000);
  pendingChunkTimes.set(focused, recent);
  const chunksPerMinute = Math.max(2, Math.ceil(15 / Math.max(1, pendingOocRounds(rosterOf(encounterFor(focused)).length, 15))));
  return recent.length < chunksPerMinute;
}
function releasePendingLeader(): void {
  const leader = pendingLeader; pendingLeader = null; leader?.release();
}
/** Capability is the delivered campaign isGM grant (owner, permissioned GM or staff),
 * plus a focused, active top-level page with both finish observers and an actor.
 * The server still checks that actor; no role is inferred from a roster owner id. */
function canDrainPending(cid: string, focused: string): boolean {
  const win = window as unknown as { ftFinishDueTimedActivities?: unknown; ftCompleteDueLongRests?: unknown; __ftGameTabIdle?: boolean };
  const encounter = encounterFor(focused);
  return win.__ftGameTabIdle !== true && (!window.top || window.top === window)
    && typeof win.ftFinishDueTimedActivities === 'function' && typeof win.ftCompleteDueLongRests === 'function'
    && !!activeActor(encounter) && upper(encounterField(encounter, 'encounter_master_parent_id')) === cid
    && inCombatOf(encounter) === false;
}
function pastGMGrace(key: string): boolean {
  if (!pendingSeenAt.has(key)) pendingSeenAt.set(key, Date.now());
  return Date.now() - pendingSeenAt.get(key)! >= PENDING_GM_GRACE_MS;
}
/** Held roster owners / explicit delegates get disjoint wall-clock slots. Rotation
 * avoids stranding an encounter when the first owner is offline. Same-owner tabs
 * arbitrate locally with Web Locks; the existing row lease arbitrates across browsers.
 * No presence fetch, server election or additional timer is needed. */
function fallbackSlot(focused: string): boolean {
  const owners = new Set<string>();
  for (const row of rosterOf(encounterFor(focused))) {
    if (Number(ecLean(row, 'encounter_creature_deleted') ?? 0) || Number(ecLean(row, 'encounter_creature_dead') ?? 0)) continue;
    for (const id of [upper(ecLean(row, 'encounter_creature_created_by_id')), upper(ftLayerValue(row, 'permissioned'))])
      if (id) owners.add(id);
  }
  const ordered = [...owners].sort();
  return ordered[Math.floor(Date.now() / 30000) % ordered.length] === upper(window.FateTide?.user?.id);
}
function pendingHints(cid: string, focused: string): PendingEntry[] {
  return [...pendingOf(campaignFor(cid)), ...pendingListed].filter(e => upper(e.encounter_id) === focused
    && PENDING_RESOLVABLE_KINDS.has(e.kind) && !pendingDone.has(upper(e.id)));
}
function reportMemberTimePending(input: TimePushRequest, debt: unknown): null {
  const interval = (asRow(debt)?.member_intervals as MemberTimeInterval[] | undefined)?.[0];
  const memberId = interval?.member_id ?? '', encounter = encounterFor(input.encounterId), member = rowFor(encounter, memberId);
  const detail = participantCompileRefusal(encounter ?? {}, member ?? null, memberId);
  return reportTimePushRefusal(input, 'member-time-pending', { memberName: detail.memberName, actorId: memberId,
    debtReason: member?._entity_refusal ? detail.rowRefusal : 'claim-in-flight' });
}
function mayDrainPending(cid: string, focused: string): boolean {
  if (!canDrainPending(cid, focused)) return false;
  if (window.FateTide?.user?.isGM === true) return true;
  const key = cid + ':' + focused;
  if (!pendingFocusSince.has(key)) pendingFocusSince.set(key, Date.now());
  const hints = pendingHints(cid, focused);
  // Observe every identity even outside this member's slot. Wakes never reset grace.
  const aged = hints.map(e => pastGMGrace('pending:' + upper(e.id)));
  // The campaign embed is capped at 20 rows. A saturated paused inventory
  // cannot establish absence of focused work and also needs bounded discovery.
  const discovery = Date.now() - Math.max(pendingFocusSince.get(key)!, pendingProgressAt.get(focused) ?? 0) >= PENDING_LIST_MS
    && Date.now() - pendingListedAt >= PENDING_LIST_MS
    && (!!runningSinceOf(campaignFor(cid)!) || pendingOf(campaignFor(cid)).length >= 20);
  return fallbackSlot(focused) && (aged.some(Boolean) || discovery);
}
function ownDueStatuses(cid: string, focused: string): Array<{ key: string; actorId: string; rest: boolean }> {
  const encounter = encounterFor(focused), campaign = campaignFor(cid), viewer = upper(window.FateTide?.user?.id);
  if (!encounter || !campaign) return [];
  const rows = rosterOf(encounter), read = readersFor(encounter);
  if (!read) return [];
  const clock = derivedClockOf(campaign);
  return rows.flatMap(row => {
    if (Number(ecLean(row, 'encounter_creature_deleted') ?? 0) || Number(ecLean(row, 'encounter_creature_dead') ?? 0)) return [];
    const controllerId = upper(ecLean(row, 'controller_encounter_creature_id'));
    const controller = controllerId ? rows.find(member => ecId(member) === controllerId) : row;
    if (!controller || !hasPlayerPermission(controller) || !(upper(ecLean(controller, 'encounter_creature_created_by_id')) === viewer
      || upper(ftLayerValue(controller, 'permissioned')) === viewer)) return [];
    return statusRows(row, 'target').flatMap(status => {
      const end = statusEndSeconds(status, read);
      return [STATUS_IDS.resting, STATUS_IDS.casting, STATUS_IDS.casting_ritual_spell, STATUS_IDS.gathering, STATUS_IDS.lockpicking,
        STATUS_IDS.reading, STATUS_IDS.training, STATUS_IDS.donning, STATUS_IDS.doffing, STATUS_IDS.disenchanting].includes(statusItemId(status) as never)
        && !statusPaused(status) && end !== null && end <= read.seconds(clock)
        ? [{ key: 'own:' + focused + ':' + ecId(row) + ':' + upper(status.status_id || statusItemId(status)) + ':' + end, actorId: ecId(row), rest: statusItemId(status) === STATUS_IDS.resting }] : [];
    });
  });
}
/** Own completion also gives the whole-boundary GM pass first refusal. A retained
 * status, including a saved compile skip, is failure, regardless of its IO verdict. */
async function finishOwnTimeActivities(cid: string, focused: string): Promise<void> {
  if (!canDrainPending(cid, focused) || window.FateTide?.user?.isGM === true) return;
  const allDue = ownDueStatuses(cid, focused);
  const due = allDue.filter(s => pastGMGrace(s.key)
    && (pendingBackoffUntil.get(s.key) ?? 0) <= Date.now());
  if (!due.length) return;
  const win = window as unknown as {
    ftFinishDueTimedActivities: (i: { encounterId: string; scope: { kind: 'ooc' }; clock: CampaignClockCols }) => Promise<void>;
    ftCompleteDueLongRests: (i: { campaignId: string; encounterId: string; clock: CampaignClockCols; actorIds: string[]; rollPrivate: number }) => Promise<string> };
  const clock = derivedClockOf(campaignFor(cid)!);
  const attempted: typeof due = [];
  try {
    attempted.push(...due.filter(s => !s.rest));
    if (due.some(s => !s.rest)) await win.ftFinishDueTimedActivities({ encounterId: focused, scope: { kind: 'ooc' }, clock });
    if (focused !== focusedEncounterId() || !canDrainPending(cid, focused)) return;
    if (due.some(s => s.rest) && !allDue.some(s => s.rest && (pendingBackoffUntil.get(s.key) ?? 0) > Date.now())) {
      attempted.push(...due.filter(s => s.rest));
      await win.ftCompleteDueLongRests({ campaignId: cid, encounterId: focused, clock,
        actorIds: [...new Set(due.filter(s => s.rest).map(s => s.actorId))], rollPrivate: 2 });
    }
  } finally {
    const retained = new Set(ownDueStatuses(cid, focused).map(s => s.key));
    for (const status of attempted) {
      if (retained.has(status.key)) backoffPending(status.key, 'own-finish', 0, null);
      else { pendingSeenAt.delete(status.key); pendingFailures.delete(status.key); pendingBackoffUntil.delete(status.key); }
    }
  }
}
/** A lock covers one drain only, scoped to the focused encounter. Never park an
 * idle/frozen tab's lock across cadences. Hidden/freeze/pagehide release promptly;
 * the server lease and held-boundary checks still protect an in-flight row. */
async function withPendingDrain(cid: string, focused: string, run: () => Promise<number>): Promise<number> {
  const locks = (navigator as unknown as { locks?: PendingLocks }).locks;
  if (!locks || typeof locks.request !== 'function') return run();
  if (!pendingLeaderMounted) {
    pendingLeaderMounted = true;
    for (const event of ['pagehide', 'freeze', 'ft:game-tab-idle']) window.addEventListener(event, releasePendingLeader);
    window.document?.addEventListener('visibilitychange', () => { if (window.document.hidden) releasePendingLeader(); });
  }
  let result = 0, entered = false, task: Promise<void> | undefined;
  try {
    await locks.request(`ft-time-pending-drain:${cid}:${focused}`, { ifAvailable: true }, async lock => {
      if (!lock) return;
      entered = true;
      let release!: () => void;
      const lifetime = new Promise<void>(resolve => { release = resolve; });
      const leader = { release }; pendingLeader = leader;
      task = run().then(value => { result = value; });
      // Releasing on freeze need not wait for a suspended task to resume.
      try { await Promise.race([task, lifetime]); }
      finally { if (pendingLeader === leader) pendingLeader = null; }
    });
  } catch (error) {
    if (entered) throw error;
    return run(); // exposed API refused by policy: retain server-lease progress
  }
  await task;
  return result;
}
const uuid = (): string => crypto.randomUUID().toUpperCase();
function pendingOf(campaign: Row | null): PendingEntry[] {
  const list = asRow(asRow(campaign?.computed)?.campaign)?.heartbeat;
  const pending = asRow(list)?.pending;
  return Array.isArray(pending) ? pending.filter((e): e is PendingEntry => !!asRow(e) && typeof (e as PendingEntry).id === 'string') : [];
}
function focusedEncounterId(): string {
  return upper(typeof window.ftGetActiveEncounterId === 'function' ? window.ftGetActiveEncounterId() : '');
}
function activeActor(encounter: Row | null): string {
  const viewer = upper(window.FateTide?.user?.id);
  if (!viewer) return '';
  const rows = rosterOf(encounter).filter(row => !Number(ecLean(row, 'encounter_creature_deleted') ?? 0)
    && !Number(ecLean(row, 'encounter_creature_dead') ?? 0));
  const gm = window.FateTide?.user?.isGM === true;
  return ecId(rows.find(row => upper(ecLean(row, 'encounter_creature_created_by_id')) === viewer || hasPlayerPermission(row))
    ?? (gm ? rows[0] : undefined) ?? {}) || (gm ? upper(encounterField(encounter, 'encounter_id')) : '');
}
async function postPending(body: Row): Promise<{ status: number; result: Row | null } | null> {
  const eid = upper(body.encounter_id || focusedEncounterId()), actor = upper(body.actor_id) || activeActor(encounterFor(eid));
  // An exact held claim can always be released, even after navigation or an actor death.
  if (!actor || (body.mode !== 'pending_release' && eid !== focusedEncounterId())) return { status: 412, result: { error: 'pending-context-changed' } };
  Object.assign(body, { schema: 'janus.time.pending.v2', encounter_id: eid, actor_id: actor,
    expected_rev: 0, operation_id: body.operation_id ?? uuid() });
  try {
    const { response, result } = await withAsyncDeadline(async signal => {
      const send = () => fetch(PENDING_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(withLogParticipants(body)), signal });
      const saving = ['pending_resolve', 'pending_member', 'pending_stalled', 'pending_finish'].includes(String(body.mode));
      const response = saving && window.ftSaveAppliedTruth ? await window.ftSaveAppliedTruth(String(body.operation_id), send) : await send();
      let result: Row | null = null;
      try { result = asRow(await response.json()); } catch { result = null; }
      return { response, result };
    }, 20000);
    if (response.status === 200 && result?.ok === true && ['pending_resolve', 'pending_member', 'pending_stalled', 'pending_finish'].includes(String(body.mode))) {
      const command = { schema: TIME_PUSH_SCHEMA, mode: 'calendar', committed: true, operation_id: String(body.operation_id),
        encounter_id: eid, actor_id: actor, campaign_id: upper(body.campaign_id || encounterField(encounterFor(eid), 'encounter_master_parent_id')),
        log_participants: body.log_participants, roll_presentations: body.roll_presentations,
        tick: asRow(result.result)?.tick as TurnTickResult | undefined, frame: { rows: {} },
        pending_closed: result.status === 'resolved' || result.status === 'void' ? [body.event_id] : [],
        ...(body.light ? { campaign_delta: { columns: asRow(body.light)?.after } } : {}) };
      handleTimeTruth(command);
      window.ftPushAppliedTruth?.({ type: 'janus_time_truth', command,
        applied_frame: buildAppliedFrame({ operation_id: String(body.operation_id), encounter_id: eid, actor_id: actor,
          rev: 0, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] }, Infinity) });
    }
    return { status: Number(result?.conflict_status ?? response.status), result };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError')
      return { status: 503, result: { ok: false, error: 'pending-save-timeout' } };
    return null;
  }
}
async function calendarFactsFor(encounterId: string): Promise<CalendarFacts | undefined> {
  const answer = await postPending({ mode: 'calendar_read', encounter_id: encounterId });
  if (answer?.status !== 200 || !Array.isArray(answer.result?.history) || !Array.isArray(answer.result?.awake_notices)) return undefined;
  return { history: answer.result.history as Row[], awakeNotices: answer.result.awake_notices.map(upper) };
}
function calendarReaders(scene: Row, campaign: Row, clock: CampaignClockCols, consent: Parameters<typeof turnReaders>[2]): EndTurnReaders {
  const read = turnReaders(scene, clock, consent), light = lightForClock(campaign, clock);
  if (read.tick) read.tick.ability = { ...read.tick.ability, lightTypesPresent: (holder, members) => encounterLightTypesPresent(
    members ? { ...scene, encounter_creatures_arr: [...members] } : scene,
    { ...campaign, campaigns: { ...asRow(campaign.campaigns), ...light } }, ftLayerValue(holder, 'encounter_light')) };
  return read;
}
async function finishCalendarObservers(campaignId: string, encounterId: string, clock: CampaignClockCols): Promise<boolean> {
  const win = window as unknown as {
    ftCompleteDueLongRests?: (i: { campaignId: string; encounterId: string; clock: CampaignClockCols; rollPrivate: number }) => Promise<string>;
    ftFinishDueTimedActivities?: (i: { encounterId: string; scope: { kind: 'ooc' }; clock: CampaignClockCols }) => Promise<void>;
  };
  if (!win.ftCompleteDueLongRests || !win.ftFinishDueTimedActivities) return false;
  const ownDue = window.FateTide?.user?.isGM === true ? [] : ownDueStatuses(campaignId, encounterId);
  if (ownDue.some(s => (pendingBackoffUntil.get(s.key) ?? 0) > Date.now())) return false;
  let completed = false;
  try {
    await win.ftFinishDueTimedActivities({ encounterId, scope: { kind: 'ooc' }, clock });
    completed = await win.ftCompleteDueLongRests({ campaignId, encounterId, clock, rollPrivate: 2 }) === 'handled';
  } finally {
    const retained = new Set(ownDueStatuses(campaignId, encounterId).map(s => s.key));
    for (const s of ownDue) if (retained.has(s.key)) backoffPending(s.key, 'own-finish', 0, null);
  }
  if (!completed) return false;
  await settleCrafts({ encounterId, clock });
  const read = readersFor(encounterFor(encounterId));
  // A failed/unsupported activity retains its row and this finish debt for the next view.
  return !!read && !rosterOf(encounterFor(encounterId)).some(row => statusRows(row, 'target').some(s =>
    [STATUS_IDS.resting, STATUS_IDS.casting, STATUS_IDS.casting_ritual_spell, STATUS_IDS.gathering, STATUS_IDS.lockpicking,
      STATUS_IDS.reading, STATUS_IDS.training, STATUS_IDS.donning, STATUS_IDS.doffing, STATUS_IDS.disenchanting].includes(statusItemId(s) as never)
      && !statusPaused(s) && statusEndSeconds(s, read) !== null && statusEndSeconds(s, read)! <= read.seconds(clock)));
}
/** Claim ONE row (the lease). Exposed on the probe for the disconnect smoke; the drain calls it too. */
async function claimPending(campaignId: string, eventId: string): Promise<{ ok: boolean; claimToken: string; status: number; result: Row | null }> {
  const claimToken = uuid();
  const actor = activeActor(encounterFor(focusedEncounterId()));
  if (!actor) return { ok: false, claimToken, status: 0, result: null };
  const r = await postPending({ schema: TIME_PUSH_SCHEMA, mode: 'pending_claim', actor_id: actor, campaign_id: upper(campaignId), event_id: upper(eventId), claim_token: claimToken, operation_id: uuid() });
  if (r?.status === 200 && r.result?.ok === true) pendingClaims.set(upper(eventId), { ...r.result, actor_id: actor });
  return { ok: r?.status === 200 && r.result?.ok === true, claimToken, status: r?.status ?? 0, result: r?.result ?? null };
}
async function resolvePending(campaignId: string, eventId: string, claimToken: string): Promise<{ ok: boolean; status: number; result: Row | null; operationId: string }> {
  const operationId = uuid();
  const debt = pendingClaims.get(upper(eventId));
  if (!debt) return { ok: false, status: 409, result: null, operationId };
  const encounterId = upper(debt.encounter_id);
  // A startup shell can already name an actor while its status-bearing members
  // are still adopting. Read the held encounter only after that bounded join.
  if (debt.kind !== MEMBER_TIME_KIND) {
    const unavailable = await awaitTimeActionRows(encounterId);
    if (unavailable) return { ok: false, status: 412,
      result: { error: 'pending-time:' + unavailable.reason, ...unavailable.detail }, operationId };
  }
  const encounter = encounterFor(encounterId), campaign = campaignFor(upper(campaignId));
  if (!encounter || !campaign || !activeActor(encounter) || inCombatOf(encounter)) return { ok: false, status: 409, result: null, operationId };
  if (debt.kind === MEMBER_TIME_KIND) {
    const intervals = debt.member_intervals as MemberTimeInterval[];
    if (!Array.isArray(intervals)) return { ok: false, status: 412, result: { error: 'member-time:interval-invalid' }, operationId };
    const interval = intervals[0], heldMember = interval && rowFor(encounter, interval.member_id);
    const member = heldMember && !Number(ecLean(heldMember, 'encounter_creature_deleted') ?? 0) ? heldMember : null;
    if (member && (member._entity_refusal || !withQuietUnported(() => entityCompileReady([ecId(member)], false)))) {
      if (interval) refusedMemberDebts.set(upper(eventId), interval.member_id);
      return { ok: false, status: 412, result: { error: 'member-time:compile-unavailable', member_id: interval?.member_id,
        memberName: interval && participantCompileRefusal(encounter, member ?? null, interval.member_id).memberName }, operationId };
    }
    let tick = emptyTurnTick(), before = {}, remaining = intervals.filter(i => i.member_id !== interval?.member_id);
    let skipped: { cause: string; memberName: string } | undefined;
    if (member && interval) {
      const scene = memberTimeScene(encounter, [member]), peers = memberTimePeers(rosterOf(encounter), member);
      const from = snapshotOf({ clock: interval.from })?.clock, to = snapshotOf({ clock: interval.to })?.clock;
      const read = calendarReaders(scene, campaign, from ?? ftClockColsFromArr(campaign), undefined);
      let cause: string | null = !from || !to ? 'interval-invalid' : null;
      try {
        if (!cause && (!Number.isInteger(interval.rounds) || interval.rounds < 1 || interval.rounds > TIME_PUSH_MAX_ROUNDS
          || read.seconds(to!) - read.seconds(from!) !== interval.rounds * 6)) cause = 'interval-invalid';
      } catch { cause = 'interval-invalid'; }
      if (!cause) {
        const charges = chargeTicksFor(member, read.abilityEntries(member), read);
        cause = holdsTaggedSummon(peers) ? 'summons' : charges.ticks.length || charges.gates.length ? 'charges'
          : memberTimeRefusal(memberTimeScene(encounter, peers), peers, read);
      }
      if (!cause && memberTimeStatusFingerprint(member) !== interval.status_fingerprint) cause = 'inputs-changed';
      let nextFingerprint = interval.status_fingerprint;
      if (!cause) {
        await prefetchTickDefinitions(scene, member);
        const pass = await resolveOocRoundPassAsync({ encounter: scene, rows: [member], rounds: interval.rounds,
          clock: from!, calendar: ftClockCalendarFromArr(campaign), readers: read,
          readersFor: (s, c) => calendarReaders(s, campaign, c, undefined) });
        if (pass?.tick.rows.some(row => row.roll_type_1 === 'janus_unported_skip')) cause = 'unsupported-expiry';
        else { tick = pass?.tick ?? tick; before = pass?.before ?? before; nextFingerprint = memberTimeStatusFingerprint(pass?.roster[0] ?? member); }
      }
      if (cause) {
        const memberName = participantCompileRefusal(encounter, member, interval.member_id).memberName;
        tick.rows.push({ ...unportedSkipRow(`${memberName}: member-time:${cause}; from ${JSON.stringify(interval.from)} to ${JSON.stringify(interval.to)}`), roll_by: interval.member_id });
        skipped = { cause, memberName };
      }
      // A skipped interval leaves current inputs untouched; the next normal pass owns overdue expiry.
      remaining = intervals.slice(1).map(i => i.member_id !== interval.member_id || cause ? i : {
        member_id: i.member_id, from: i.from, to: i.to, rounds: i.rounds, status_fingerprint: nextFingerprint });
    }
    const body = { mode: 'pending_member', event_id: eventId, claim_token: claimToken, operation_id: operationId,
      encounter_id: encounterId, coalesced: Number(debt.coalesced ?? 0), rounds: 1,
      member_remaining: remaining, tick, tick_before_ec: before };
    let answer = await postPending(body);
    if (!answer || answer.status >= 500) answer = await postPending(body);
    if (answer?.result?.ok) {
      applyTimeFrame(encounterId, operationId, { rows: {} }, asRow(answer.result.result)?.tick as TurnTickResult);
      if (skipped && interval) skipUnported('member-time:' + skipped.cause, { actorId: interval.member_id,
        memberName: skipped.memberName, from: interval.from, to: interval.to });
      pendingClaims.delete(upper(eventId));
      refusedMemberDebts.delete(upper(eventId));
    }
    return { ok: answer?.result?.ok === true, status: answer?.status ?? 0, result: answer?.result ?? null, operationId };
  }
  if (debt.finishing === true) {
    if (!await finishCalendarObservers(campaignId, encounterId, ftClockColsFromArr(campaign))) return { ok: false, status: 409, result: null, operationId };
    const body = { mode: 'pending_finish', event_id: eventId, claim_token: claimToken, operation_id: operationId, coalesced: Number(debt.coalesced ?? 0), encounter_id: encounterId };
    let answer = await postPending(body);
    if (!answer || answer.status >= 500) answer = await postPending(body);
    if (answer?.result?.ok) pendingClaims.delete(upper(eventId));
    return { ok: answer?.result?.ok === true, status: answer?.status ?? 0, result: answer?.result ?? null, operationId };
  }
  const calendarFacts = await calendarFactsFor(encounterId);
  if (debt.kind === 'checkpoint_heartbeat_stalled')
    await Promise.all(rosterOf(encounter).map(row => prefetchTickDefinitions(encounter, row)));
  let deathResources = await prepareRoundDeaths(encounter, campaign);
  if (debt.kind === 'checkpoint_heartbeat_stalled') {
    const clock = ftClockColsFromArr(campaign), calendar = ftClockCalendarFromArr(campaign);
    const read = calendarReaders(encounter, campaign, clock, undefined);
    if (read.tick && deathResources) read.tick.ability = { ...read.tick.ability, deathResources };
    const tick = resolveCalendarCatchup({ encounter, roster: rosterOf(encounter), clock, calendar, read,
      facts: calendarFacts, preflightOnly: false, calendarOnly: true });
    const before = prepareTurnTick(rosterOf(encounter), tick, read.abilityEntries).before;
    const removed = new Set([...tick.statusesRemoved.map(s => upper(s.status_id)),
      ...(tick.spawnRows?.writes ?? []).filter(w => w.table === 'statuses' && w.row.status_deleted === 1).map(w => upper(w.row.status_id))]);
    const heldIds = new Set(rosterOf(encounter).flatMap(row => statusRows(row, 'target').map(s => upper(s.status_id))));
    const ack = (Array.isArray(debt.status_ids) ? debt.status_ids.map(upper) : []).filter(id => !heldIds.has(id) || removed.has(id));
    if (!ack.length) return { ok: false, status: 409, result: null, operationId };
    const body = { mode: 'pending_stalled', event_id: eventId, claim_token: claimToken, operation_id: operationId,
      encounter_id: encounterId, coalesced: Number(debt.coalesced ?? 0), rounds: 1, ack_status_ids: ack, tick, tick_before_ec: before };
    let answer = await postPending(body); if (!answer || answer.status >= 500) answer = await postPending(body);
    if (answer?.result?.ok) { applyTimeFrame(encounterId, operationId, { rows: {} }, asRow(answer.result.result)?.tick as TurnTickResult); pendingClaims.delete(upper(eventId)); }
    return { ok: answer?.result?.ok === true, status: answer?.status ?? 0, result: answer?.result ?? null, operationId };
  }
  const calendar = ftClockCalendarFromArr(campaign);
  const span = planPendingTimeSpan(debt, ftClockColsFromArr(campaign), calendar, rosterOf(encounter).length);
  const rounds = span.rounds;
  let consent = createTriggerConsent(encounterId);
  const plan = (preflightOnly: boolean): ReturnType<typeof resolveOocRoundPassAsync> => resolveOocRoundPassAsync({ encounter, rows: rosterOf(encounter), rounds, calendar,
    clock: span.clock, fastForward: span.fastForward, calendarFacts,
    afterRound: rows => {
      const read = turnReaders(encounter);
      return rows.flatMap(row => chargeTicksFor(row, read.abilityEntries(row), read).ticks);
    },
    weariness: Number(_ftCamField(campaign, 'waking_weariness_enabled') ?? 0) === 1, readers: turnReaders(encounter), preflightOnly,
    readersFor: (scene, clock) => {
      const read = calendarReaders(scene, campaign, clock, consent);
      if (read.tick && deathResources) read.tick.ability = { ...read.tick.ability, deathResources };
      return read;
    } });
  // A cold payload elsewhere on a member is not a prerequisite for a supported
  // held expiry. Warm only after the native dry run reaches unavailable work.
  let warmed = false;
  const warm = async (): Promise<void> => {
    warmed = true;
    await withAsyncDeadline(async () => {
      await Promise.all(rosterOf(encounter).map(row => prefetchTickDefinitions(encounter, row)));
      deathResources = await prepareRoundDeaths(encounter, campaign);
    }, 20000);
  };
  try {
    await planWithConsent(encounterId, activeActor(encounter), async c => {
      consent = c;
      const preview = async () => {
        try { return { pass: await plan(true), asks: false }; }
        catch (error) { if (error instanceof HandBack && error.reason.includes('trigger-consent')) return { pass: null, asks: true }; throw error; }
      };
      let result;
      try { result = await preview(); }
      catch (error) { if (warmed) throw error; await warm(); return preview(); }
      if (!warmed && result.pass?.tick.rows.some(row => row.roll_type_1 === 'janus_unported_skip')) {
        await warm(); return preview();
      }
      return result;
    }, result => result.asks);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError')
      return { ok: false, status: 412, result: { error: 'pending-inputs-timeout' }, operationId };
    throw error;
  }
  const pass = await plan(false);
  const fortunateOffers = pass?.tick.fortunateOffers ?? [];
  if (pass) delete pass.tick.fortunateOffers;
  const skipKeys = new Set(Array.isArray(debt.skip_keys) ? debt.skip_keys.map(String) : []);
  if (pass) pass.tick.rows = pass.tick.rows.filter(row => {
    if (row.roll_type_1 !== 'janus_unported_skip') return true;
    const key = upper(row.roll_by) + '|' + row.rolls_full;
    if (skipKeys.has(key)) return false;
    skipKeys.add(key); return true;
  });
  const body = { schema: TIME_PUSH_SCHEMA, mode: 'pending_resolve', rules_version: TIME_PUSH_RULES_CALENDAR,
    campaign_id: upper(campaignId), encounter_id: encounterId, event_id: upper(eventId), claim_token: claimToken, operation_id: operationId,
    rounds, span_remaining: pendingTimeAcknowledgment(rounds, span.remaining, pass?.tick ?? emptyTurnTick(), Number(debt.coalesced ?? 0)),
    skip_keys: [...skipKeys], light: { before: { campaign_light: _ftCamField(campaign, 'campaign_light') ?? '', campaign_sun_state: _ftCamField(campaign, 'campaign_sun_state') ?? '' },
      after: { campaign_light: lightForClock(campaign, derivedClockOf(campaign)).campaign_light, campaign_sun_state: lightForClock(campaign, derivedClockOf(campaign)).campaign_sun_state } },
    coalesced: Number(debt.coalesced ?? 0), tick: pass?.tick ?? emptyTurnTick(), tick_before_ec: pass?.before ?? {} };
  let r = await postPending(body);
  if (r === null || r.status >= 500) r = await postPending(body); // same operation id: the durable receipt answers if it landed
  if (r?.status === 200 && r.result?.ok === true) {
    const saved = asRow(r.result.result);
    applyTimeFrame(encounterId, operationId, { rows: {} }, saved?.tick as TurnTickResult | undefined);
    offerTimeFortunate(encounterId, operationId, fortunateOffers);
    pendingClaims.delete(upper(eventId));
  }
  return { ok: r?.status === 200 && r.result?.ok === true, status: r?.status ?? 0, result: r?.result ?? null, operationId };
}
/** Return completed events; acknowledged segments and finish preparation remain eligible without counting twice. */
export async function drainPendingTimeEvents(campaignId?: string): Promise<number> {
  if (pendingDraining) return 0;
  if (window.ftBaseCommitsPaused?.()) { releasePendingLeader(); return 0; }
  if ((window as unknown as { __ftTimePendingHold?: boolean }).__ftTimePendingHold === true) { releasePendingLeader(); return 0; }
  const cid = upper(campaignId ?? (window.FateTide as unknown as { campaign?: { id?: unknown } } | undefined)?.campaign?.id);
  const focused = focusedEncounterId();
  if (!cid || !focused) { releasePendingLeader(); return 0; }
  pendingDraining = true; // before the first await: two polls never overlap
  try {
    const shared = mayDrainPending(cid, focused);
    if (!canDrainPending(cid, focused)) return 0;
    return await withPendingDrain(cid, focused, async () => {
      const resolved = shared ? await drainOnce(cid) : 0;
      await finishOwnTimeActivities(cid, focused);
      return resolved;
    });
  }
  finally {
    pendingDraining = false;
    if (focused !== focusedEncounterId() || !canDrainPending(cid, focused)) releasePendingLeader();
  }
}
async function drainOnce(cid: string): Promise<number> {
  let resolved = 0;
  const focused = focusedEncounterId();
  const heldBoundary = (): boolean => {
    return focused === focusedEncounterId() && canDrainPending(cid, focused);
  };
  const run = async (): Promise<void> => {
    if (!heldBoundary()) return;
    if (!canPlanPendingChunk(focused) && !pendingListed.some(e => e.finishing && !pendingDone.has(upper(e.id)))) return;
    const listKey = 'list:' + focused;
    if ((pendingBackoffUntil.get(listKey) ?? 0) > Date.now()) return;
    if (pendingListedEncounter !== focused) { pendingListedEncounter = focused; pendingListedAt = 0; pendingListed = []; }
    // The embed is only a wake-up hint. Claim candidates always come from a CURRENT
    // list obtained while holding the same Web Lock as the claim and resolve.
    const hints = pendingHints(cid, focused);
    for (const [id, memberId] of refusedMemberDebts) {
      const row = rowFor(encounterFor(focused), memberId);
      if (!row || Number(ecLean(row, 'encounter_creature_deleted') ?? 0) || !row._entity_refusal && withQuietUnported(() => entityCompileReady([memberId], false))) {
        refusedMemberDebts.delete(id); pendingBackoffUntil.delete(id); pendingFailures.delete(id);
      }
    }
    const hinted = hints.some(e => (pendingBackoffUntil.get(upper(e.id)) ?? 0) <= Date.now()
      && Number(e.lease_until ?? 0) <= Date.now());
    // A five-minute backstop discovers missing hints; it cannot override a known
    // lease or a failed row's retry deadline. Campaign updates never reset either.
    if (!hinted && (hints.length > 0 || Date.now() - pendingListedAt < PENDING_LIST_MS)) return;
    let retryConflicts = 0;
    while (heldBoundary()) {
      if (!canPlanPendingChunk(focused) && !pendingListed.some(e => e.finishing && !pendingDone.has(upper(e.id)))) return;
      const listed = await postPending({ mode: 'pending_list', encounter_id: focused });
      if (listed?.status !== 200 || !Array.isArray(listed.result?.pending)) {
        backoffPending(listKey, 'list', listed?.status ?? 0, listed?.result ?? null); return;
      }
      pendingFailures.delete(listKey); pendingBackoffUntil.delete(listKey);
      const currentIds = new Set((listed.result.pending as PendingEntry[]).map(e => upper(e.id)));
      for (const hint of [...pendingOf(campaignFor(cid)), ...pendingListed])
        if (upper(hint.encounter_id) === focused && !currentIds.has(upper(hint.id))) pendingDone.add(upper(hint.id));
      pendingListedAt = Date.now();
      pendingListed = listed.result.pending as PendingEntry[];
      let progressed = false;
      for (const entry of pendingListed) {
        if (!heldBoundary()) return;
        const id = upper(entry.id);
        if (upper(entry.encounter_id) !== focused || !PENDING_RESOLVABLE_KINDS.has(entry.kind) || pendingDone.has(id)) continue;
        if (entry.lease_live || Number(entry.lease_until ?? 0) >= Date.now()) {
          pendingBackoffUntil.set(id, Number(entry.lease_until) || Date.now() + 90000); continue;
        }
        if ((pendingBackoffUntil.get(id) ?? 0) > Date.now()) continue;
        if (!entry.finishing && !canPlanPendingChunk(focused)) continue;
        if (window.FateTide?.user?.isGM !== true && !pastGMGrace('pending:' + id)) continue;
        emit('ft:time-pending-claiming', { event: entry });
        const claim = await claimPending(cid, id);
        if (!claim.ok) {
          if (['resolved', 'void'].includes(String(claim.result?.status ?? ''))) pendingDone.add(id);
          // Another browser can win after the list. Its ordinary CAS miss is a 200
          // response; the next poll reads the current lease rather than guessing.
          backoffPending(entry, 'claim', claim.status, claim.result);
          continue;
        }
        const heldClaim = pendingClaims.get(id);
        const release = async (): Promise<void> => {
          await postPending({ mode: 'pending_release', event_id: id, claim_token: claim.claimToken,
            encounter_id: focused, actor_id: heldClaim?.actor_id });
          pendingClaims.delete(id);
        };
        if (!heldBoundary()) { await release(); return; }
        emit('ft:time-pending-claimed', { event: entry, claim_token: claim.claimToken });
        let done: Awaited<ReturnType<typeof resolvePending>>;
        try { done = await resolvePending(cid, id, claim.claimToken); }
        catch (error) {
          await release();
          backoffPending(entry, 'resolve', 0, { error: String(error instanceof Error ? error.message : error) });
          continue;
        }
        if (done.ok) {
          if (!heldClaim?.finishing && heldClaim?.kind === 'encounter_pass1' && typeof asRow(done.result?.result)?.remaining === 'number') {
            pendingChunkTimes.get(focused)!.push(Date.now() - Date.now() % PENDING_POLL_MS);
            entry.finishing = asRow(done.result?.result)?.finishing === true || Number(asRow(done.result?.result)?.remaining) === 0;
          }
          pendingFailures.delete(id); pendingBackoffUntil.delete(id);
          if (done.result?.status !== 'pending') { pendingDone.add(id); resolved++; }
          // A pending answer still owes another segment or the final observers.
          // Terminal acknowledgment already closes this exact row; no empty list
          // is needed before waiting for a new hint or the discovery backstop.
          if (done.result?.status === 'pending') progressed = true;
          retryConflicts = 0;
          emit('ft:time-pending-resolved', { event: entry, operation_id: done.operationId, result: done.result });
        } else {
          // A definite refusal saved nothing. Release now; a new generation or CAS
          // snapshot can be planned on the next task instead of waiting 90 seconds.
          if (done.status > 0 && done.status < 500) await release();
          const retryable = entry.kind !== MEMBER_TIME_KIND && ['pending-debt-changed', 'calendar-light-changed', 'stale'].includes(String(done.result?.error));
          if (retryable && retryConflicts++ < 3) {
            if (done.result?.error !== 'pending-debt-changed') {
              const authoritative = asRow(done.result?.authoritative);
              const refused = await refreshTimeConflict(focused, authoritative, done.operationId ?? id);
              if (refused) { backoffPending(entry, 'resolve', done.status, { ...done.result, error: refused }, done.operationId); continue; }
              applyTimeCampaignDelta(cid, { columns: { campaign_light: authoritative?.campaign_light,
                campaign_sun_state: authoritative?.campaign_sun_state } });
            }
            progressed = true;
          } else backoffPending(entry, 'resolve', done.status, done.result, done.operationId);
        }
        await yieldTimeTask();
      }
      if (!progressed) return;
      await yieldTimeTask();
    }
  };
  await run();
  return resolved;
}
if (typeof window !== 'undefined' && typeof window.setInterval === 'function') {
  const observeZones = (): void => {
    const campaignId = upper((window.FateTide as unknown as { campaign?: { id?: unknown } } | undefined)?.campaign?.id);
    if (campaignId) void expireObservedZones(campaignId).catch(error => console.warn('zone expiry:', error));
  };
  window.setInterval(() => { void drainPendingTimeEvents(); observeZones(); }, PENDING_POLL_MS);
  for (const event of ['ft:campaign-state-received', 'ft:campaign-clock-installed', 'ft:encounter-state-received'])
    window.addEventListener(event, () => { window.setTimeout(() => { void drainPendingTimeEvents(); }, 250); });
  for (const event of ['ft:campaign-state-received', 'ft:encounter-state-received'])
    window.addEventListener(event, observeZones);
}

Object.assign(window, { ftApplyCampaignClockDelta: handleTimeTruth, ftTimeRoundDue: timeRoundDue, ftTryCommitTimePush: tryCommitTimePush, ftRetryUnresolvedTimePush: retryUnresolvedTimePush, ftHasUnresolvedTimePush: hasUnresolvedTimePush, ftTryCommitHungerHeartbeat: tryCommitHungerHeartbeat, ftTimeTruthSchema: TIME_PUSH_SCHEMA, ftTimeTruthProbe: { dryRun: dryRunTimePush },
  ftTimePendingProbe: { claim: claimPending, resolve: resolvePending, drain: drainPendingTimeEvents, list: (cid?: string) => pendingOf(campaignFor(upper(cid ?? (window.FateTide as unknown as { campaign?: { id?: unknown } } | undefined)?.campaign?.id))) } });
